import { useEffect, useState, useCallback, type ReactNode } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { notifications } from '@mantine/notifications'
import { urlToBase64, drawHeader, registerThaiFont, renderReportPdf, PDF_FONT } from '../assets/pdfHelper'
import jsPDF from 'jspdf'
import { useBreakpoint } from '../hooks/useBreakpoint'
import {
  Box, Text, Select, Group, Badge, Loader, Center,
  SimpleGrid, Tabs, Button, Stack, Paper, Divider,
  Table, Progress, ScrollArea, TextInput, Card, ActionIcon,
} from '@mantine/core'
import {
  IconFileText, IconTool, IconArrowsExchange, IconChartBar,
  IconPackage, IconKey, IconDownload, IconPrinter, IconFilter,
  IconLayoutDashboard, IconClipboardCheck, IconArrowsTransferDown,
  IconSearch, IconCalendar, IconX, IconTicket, IconBottle,
  IconStar, IconStarFilled, IconClock, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react'
import {
  getDepreciationReport, getAssetListReport,
  getRepairJobs, getAllBorrows, getHandovers,
  getInventoryItems, getInventorySummary,
  getLicenses, getLicenseSummary,
  getDashboard, getDepartments,
  getCheckSessions, getTransfers,
} from '../api/client'
import SmartDateInput from '../components/SmartDateInput'

// ── Helpers ────────────────────────────────────────────────────────────────
function exportCSV(filename: string, rows: any[], columns: { key: string; label: string }[]) {
  const header = columns.map(c => `"${c.label}"`).join(',')
  const body = rows.map(row =>
    columns.map(c => {
      const val = c.key.split('.').reduce((o: any, k: string) => o?.[k], row) ?? ''
      return `"${String(val).replace(/"/g, '""')}"`
    }).join(',')
  ).join('\n')
  const blob = new Blob(['\uFEFF' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' })
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob)
  a.download = filename; a.click()
}


/**
 * Repair Report โดยเฉพาะ — ต่างจากรายงานอื่นเพราะต้องมี "รายละเอียดการซ่อม + รูปถ่าย" ต่องาน
 * ซึ่ง autotable ทำแบบ cell-per-image ให้สวยไม่ได้ง่ายๆ เลยวาดเป็น "การ์ดต่องาน" เองแทน
 * แต่ละงาน: บรรทัดหลัก (code/asset/type/status/cost/date) + บรรทัด resolution/note + รูปแรก 1 รูปเล็กๆ
 */
async function printRepairReport(jobs: any[], logoUrl: string | null | undefined, companyName: string | null | undefined, t: any) {
  notifications.show({ id: 'pdf-gen', loading: true, message: t('reports.generatingPdf', 'Generating PDF...'), autoClose: false, withCloseButton: false })
  try {
    const logoDataUrl = logoUrl ? await urlToBase64(logoUrl).catch(() => null) : null
    const pageW = 297, pageH = 210, M = 15
    const pdf = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' })
    registerThaiFont(pdf)

    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    const ref = `RPT-REP-${Date.now().toString().slice(-6)}`
    let y = await drawHeader(pdf, t('reports.repairReportTitle', 'Repair Report'), '', ref, today, logoDataUrl, companyName, pageW)

    const PHOTO_W = 24, PHOTO_H = 24, ROW_MIN_H = 26  // mm — ขนาดรูปเล็กกะทัดรัดตามที่ขอ
    const contentW = pageW - M * 2

    for (const j of jobs) {
      // preload รูปแรก (ถ้ามี) ก่อน คำนวณความสูงจริงของ card ต้องรู้ว่ามีรูปไหม
      let photoDataUrl: string | null = null
      if (Array.isArray(j.images) && j.images[0]) {
        photoDataUrl = await urlToBase64(j.images[0]).catch(() => null)
      }

      // ประมาณความสูง card: บรรทัดหลัก + บรรทัด resolution (wrap ได้ถึง 2 บรรทัด) + padding
      pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(8)
      const noteText = [j.resolution, j.note].filter(Boolean).join(' — ') || t('reports.noResolutionNote', 'No resolution/note recorded')
      const noteLines = pdf.splitTextToSize(noteText, contentW - (photoDataUrl ? PHOTO_W + 6 : 0) - 4)
      const cardH = Math.max(ROW_MIN_H, 8 + noteLines.length * 3.6 + 4, photoDataUrl ? PHOTO_H + 6 : 0)

      if (y + cardH > pageH - M) { pdf.addPage(); y = M }

      // การ์ดกรอบบางๆ
      pdf.setDrawColor(226, 232, 240)
      pdf.rect(M, y, contentW, cardH)

      const textX = M + 4
      const textW = contentW - 8 - (photoDataUrl ? PHOTO_W + 4 : 0)

      pdf.setFont(PDF_FONT, 'bold'); pdf.setFontSize(9); pdf.setTextColor(0, 0, 0)
      const brandModel = [j.asset?.brand, j.asset?.model].filter(Boolean).join(' ')
      pdf.text(`${j.assetCode ?? '—'}  •  ${j.assetName ?? '—'}${brandModel ? `  •  Brand / Model: ${brandModel}` : ''}`, textX, y + 6)

      pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(0, 0, 0)
      const metaLine = `${t('common.type','Type')}: ${j.type ?? '—'}   ${t('common.status','Status')}: ${j.status ?? '—'}   ${t('reports.estCost','Est. Cost')}: ${j.estimatedCost ? '฿'+Number(j.estimatedCost).toLocaleString() : '—'}   ${t('common.date','Date')}: ${j.createdAt?.slice(0,10) ?? '—'}`
      pdf.text(metaLine, textX, y + 11)

      pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(7.5); pdf.setTextColor(0, 0, 0)
      pdf.text(noteLines, textX, y + 16)

      if (photoDataUrl) {
        try {
          const ext = photoDataUrl.includes('image/png') ? 'PNG' : 'JPEG'
          pdf.addImage(photoDataUrl, ext, M + contentW - PHOTO_W - 3, y + 3, PHOTO_W, PHOTO_H, undefined, 'FAST')
        } catch (_) { /* ข้ามถ้ารูปโหลดไม่ได้ ไม่ทำให้ทั้ง PDF พัง */ }
      }

      y += cardH + 4
    }

    const blob = pdf.output('blob')
    const url = URL.createObjectURL(blob)
    const tab = window.open(url, '_blank')
    if (tab) setTimeout(() => URL.revokeObjectURL(url), 30_000)
  } finally {
    notifications.hide('pdf-gen')
  }
}

async function printSection(id: string, title: string, subtitle: string | undefined, logoUrl: string | null | undefined, companyName: string | null | undefined, t: any) {
  const el = document.getElementById(id)
  if (!el) return

  notifications.show({ id: 'pdf-gen', loading: true, message: t('reports.generatingPdf', 'Generating PDF...'), autoClose: false, withCloseButton: false })

  try {
    const logoDataUrl = logoUrl ? await urlToBase64(logoUrl).catch(() => null) : null

    // A4 แนวนอน — รายงานมีตารางกว้าง ต้องการพื้นที่แนวนอนมากกว่าปกติ
    const pageW = 297, pageH = 210, M = 15
    const pdf = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' })
    registerThaiFont(pdf)

    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    const ref = `RPT-${Date.now().toString().slice(-6)}`
    const y = await drawHeader(pdf, title, subtitle ?? '', ref, today, logoDataUrl, companyName, pageW)

    // กราฟ→ภาพ, ตาราง→autotable จริง (หัวตารางซ้ำทุกหน้าอัตโนมัติ, ตัวอักษรดำล้วน)
    await renderReportPdf(pdf, el, y, pageW, pageH, M)

    // Blob URL → เบราว์เซอร์เปิดเป็น native PDF viewer (zoom/print/download/เลือกหน้าในตัว)
    const blob = pdf.output('blob')
    const url = URL.createObjectURL(blob)
    const tab = window.open(url, '_blank')
    if (tab) setTimeout(() => URL.revokeObjectURL(url), 30_000)
  } finally {
    notifications.hide('pdf-gen')
  }
}

const thStyle = { fontSize: 11, color: '#94a3b8', fontWeight: 500 as const, textTransform: 'uppercase' as const, letterSpacing: '0.3px' }
const STATUS_COLOR: Record<string, string> = { AVAILABLE: 'teal', BORROWED: 'blue', ASSIGNED: 'indigo', INSTALLED: 'cyan', REPAIR: 'orange', DAMAGED: 'red', LOST: 'violet', WRITE_OFF: 'gray' }

// ── การ์ดรายงานสำหรับมือถือ ใช้ร่วมกันทุกแท็บ — โชว์แค่ฟิลด์หลัก (title/subtitle/badge) เสมอ
// ส่วนที่เหลือ (details) พับไว้ กดที่การ์ดเพื่อขยายดูครบทุกคอลัมน์เหมือนตาราง desktop โดยไม่ต้อง
// ตัดข้อมูลออกไปเลย — ตามที่ตกลงกันไว้ว่าอยากได้การ์ด ไม่อยากเสียข้อมูลไปจากที่เห็นในตาราง ──
function ReportCard({ title, subtitle, badge, details }: {
  title: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  details: { label: string; value: ReactNode }[]
}) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Card withBorder radius="lg" p="sm" mb={8} onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" fw={600} lineClamp={1}>{title}</Text>
          {subtitle && <Text size="xs" c="dimmed" lineClamp={1} mt={1}>{subtitle}</Text>}
        </Box>
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          {badge}
          <ActionIcon variant="subtle" color="gray" size="sm" style={{ pointerEvents: 'none' }}>
            {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          </ActionIcon>
        </Group>
      </Group>
      {expanded && details.length > 0 && (
        <>
          <Divider my={8} />
          <Stack gap={5}>
            {details.map((d, i) => (
              <Group key={i} gap={6} wrap="nowrap" align="flex-start">
                <Text size="xs" c="dimmed" style={{ minWidth: 92, flexShrink: 0 }}>{d.label}</Text>
                <Box style={{ fontSize: 12, minWidth: 0 }}>{d.value}</Box>
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Card>
  )
}
// เวอร์ชัน hex สำหรับกราฟ (recharts ต้องการ CSS color จริง ไม่ใช่ชื่อสี Mantine) — ใช้ palette เดียวกับ DashboardPage.tsx
const STATUS_HEX: Record<string, string> = {
  AVAILABLE: '#0d9488', BORROWED: '#6366f1', ASSIGNED: '#3b82f6', INSTALLED: '#06b6d4',
  REPAIR: '#f59e0b', DAMAGED: '#ef4444', LOST: '#8b5cf6', WRITE_OFF: '#94a3b8',
}
const BORROW_COLOR: Record<string, string> = { PENDING: 'yellow', APPROVED: 'teal', REJECTED: 'red', RETURNED: 'gray', PARTIALLY_RETURNED: 'orange', OVERDUE: 'red' }
const LIC_COLOR: Record<string, string> = { ACTIVE: 'teal', EXPIRING: 'orange', EXPIRED: 'red', INACTIVE: 'gray' }

function KPI({ label, value, color = '#0f172a', sub }: { label: string; value: any; color?: string; sub?: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 4 }}>{label}</Text>
      <Text style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'Inter,sans-serif', letterSpacing: '-0.5px', lineHeight: 1 }}>{value}</Text>
      {sub && <Text size="xs" c="dimmed" mt={2}>{sub}</Text>}
    </Paper>
  )
}

function SecHeader({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <Group gap={8} my="md">
      <Box style={{ color: '#0d9488' }}>{icon}</Box>
      <Text fw={700} size="sm" style={{ fontFamily: 'Inter,sans-serif' }}>{title}</Text>
      <Box style={{ flex: 1, height: 1, background: '#e2e8f0', alignSelf: 'center' }} />
    </Group>
  )
}

/** Donut chart — สัดส่วนตามสถานะ พร้อมตัวเลขรวมตรงกลาง ใช้ที่เดียวได้หลายจุด (Summary + Breakdown) */
function StatusDonut({ data, total, colorMap, labelMap, centerLabel, height = 200 }: {
  data: Record<string, number>; total: number; colorMap: Record<string, string>
  labelMap?: Record<string, string>; centerLabel: string; height?: number
}) {
  const entries = Object.entries(data).filter(([, v]) => v > 0).sort(([, a], [, b]) => (b as number) - (a as number))
  const chartData = entries.map(([k, v]) => ({ name: labelMap?.[k] ?? k, key: k, value: v }))
  if (entries.length === 0) return <Center h={height}><Text size="xs" c="dimmed">—</Text></Center>
  return (
    <Group align="center" gap="lg" wrap="nowrap">
      <Box style={{ position: 'relative', width: height, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="90%" paddingAngle={2} stroke="none">
              {chartData.map((d, i) => <Cell key={i} fill={colorMap[d.key] ?? '#94a3b8'} />)}
            </Pie>
            <Tooltip formatter={(v: any, n: any) => [v, n]} />
          </PieChart>
        </ResponsiveContainer>
        <Box style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Text style={{ fontSize: 26, fontWeight: 700, fontFamily: 'Inter,sans-serif', color: '#0f172a', lineHeight: 1 }}>{total}</Text>
          <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', marginTop: 2 }}>{centerLabel}</Text>
        </Box>
      </Box>
      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
        {entries.map(([k, v]) => (
          <Group key={k} justify="space-between" gap={8} wrap="nowrap">
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              <Box style={{ width: 8, height: 8, borderRadius: 2, background: colorMap[k] ?? '#94a3b8', flexShrink: 0 }} />
              <Text size="xs" truncate>{labelMap?.[k] ?? k}</Text>
            </Group>
            <Text size="xs" fw={600} c="dimmed" style={{ flexShrink: 0 }}>{v} · {total ? Math.round((v as number) / total * 100) : 0}%</Text>
          </Group>
        ))}
      </Stack>
    </Group>
  )
}

/** Vertical bar chart — สำหรับข้อมูลตามเวลา (เช่น ค่าซ่อมรายเดือน) อ่านซ้าย→ขวาตามลำดับเวลา */
function TrendBarChart({ data, color = '#6366f1', height = 200, valuePrefix = '' }: {
  data: Record<string, number>; color?: string; height?: number; valuePrefix?: string
}) {
  const chartData = Object.entries(data).map(([name, value]) => ({ name, value }))
  const hasData = chartData.some(d => d.value > 0)
  if (!hasData) return <Center h={height}><Text size="xs" c="dimmed">—</Text></Center>
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <YAxis hide />
        <Tooltip formatter={(v: any) => [`${valuePrefix}${Number(v).toLocaleString()}`, '']} labelStyle={{ fontSize: 12 }} />
        <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} barSize={28} />
      </BarChart>
    </ResponsiveContainer>
  )
}
function TopBarChart({ data, limit = 8, color = '#0d9488', height = 220 }: {
  data: Record<string, number>; limit?: number; color?: string; height?: number
}) {
  const chartData = Object.entries(data)
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, limit)
    .map(([name, value]) => ({ name: name.length > 18 ? name.slice(0, 17) + '…' : name, value }))
    .reverse()
  if (chartData.length === 0) return <Center h={height}><Text size="xs" c="dimmed">—</Text></Center>
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} layout="vertical" margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v: any) => [v, '']} labelStyle={{ fontSize: 12 }} />
        <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} barSize={14} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function PrintBtn({ targetId, title, subtitle }: { targetId: string; title: string; subtitle?: string }) {
  const { t } = useTranslation()
  const { companyLogoUrl, companyName } = useAuth()
  return (
    <Button size="xs" variant="light" color="gray" leftSection={<IconPrinter size={13} />}
      onClick={() => printSection(targetId, title, subtitle, companyLogoUrl, companyName, t)}>{t("common.print","Print")}</Button>
  )
}

function CSVBtn({ label, rows, columns }: { label: string; rows: any[]; columns: { key: string; label: string }[] }) {
  const { t } = useTranslation()
  return (
    <Button size="xs" variant="light" color="teal" leftSection={<IconDownload size={13} />}
      onClick={() => exportCSV(label, rows, columns)}>{t("reports.csv","CSV")}</Button>
  )
}

// ══════════════════════════════════════════════════════════════════════════
// ── Report Hub metadata ───────────────────────────────────────────────────
// รวมทุกอย่างที่ Hub ต้องใช้ต่อรายงาน 1 รายการ — เพิ่มรายงานใหม่แค่เพิ่ม entry ที่นี่
const REPORT_CATEGORIES = [
  { key: 'assets',    labelKey: 'reports.catAssets',    icon: IconPackage,   color: '#0d9488' },
  { key: 'service',   labelKey: 'reports.catService',   icon: IconTool,      color: '#dc2626' },
  { key: 'movement',  labelKey: 'reports.catMovement',  icon: IconArrowsExchange, color: '#7c3aed' },
  { key: 'other',     labelKey: 'reports.catOther',     icon: IconFileText,  color: '#64748b' },
]
const REPORT_META: { key: string; icon: any; labelKey: string; descKey: string; category: string }[] = [
  { key: 'summary',      icon: IconLayoutDashboard,     labelKey: 'common.summary',        descKey: 'reports.descSummary',      category: 'assets' },
  { key: 'assets',       icon: IconFileText,            labelKey: 'reports.assets',         descKey: 'reports.descAssets',       category: 'assets' },
  { key: 'breakdown',    icon: IconChartBar,            labelKey: 'reports.breakdown',      descKey: 'reports.descBreakdown',    category: 'assets' },
  { key: 'depreciation', icon: IconChartBar,            labelKey: 'reports.depreciation',   descKey: 'reports.descDepreciation', category: 'assets' },
  { key: 'checking',     icon: IconClipboardCheck,      labelKey: 'nav.assetChecking',      descKey: 'reports.descChecking',     category: 'assets' },
  { key: 'repair',       icon: IconTool,                labelKey: 'reports.repair',         descKey: 'reports.descRepair',       category: 'service' },
  { key: 'workorders',   icon: IconTicket,              labelKey: 'reports.workorders',     descKey: 'reports.descWorkorders',   category: 'service' },
  { key: 'borrow',       icon: IconArrowsExchange,      labelKey: 'reports.borrow',         descKey: 'reports.descBorrow',       category: 'movement' },
  { key: 'handover',     icon: IconFileText,            labelKey: 'reports.handover',       descKey: 'reports.descHandover',     category: 'movement' },
  { key: 'transfer',     icon: IconArrowsTransferDown,  labelKey: 'reports.transfer',       descKey: 'reports.descTransfer',     category: 'movement' },
  { key: 'inventory',    icon: IconPackage,             labelKey: 'nav.inventory',          descKey: 'reports.descInventory',    category: 'other' },
  { key: 'license',      icon: IconKey,                 labelKey: 'nav.licenses',           descKey: 'reports.descLicense',      category: 'other' },
]
const RECENTS_KEY = 'assetos.reports.recents'
const FAVORITES_KEY = 'assetos.reports.favorites'
const MAX_RECENTS = 4

function ReportHub({
  t, hubSearch, setHubSearch, hubCategory, setHubCategory,
  hubFilteredReports, recents, favorites, openReport, toggleFavorite,
}: any) {
  const recentMeta = recents.map((k: string) => REPORT_META.find(r => r.key === k)).filter(Boolean)
  const favoriteMeta = favorites.map((k: string) => REPORT_META.find(r => r.key === k)).filter(Boolean)

  const ReportCard = ({ r, small }: { r: typeof REPORT_META[0]; small?: boolean }) => {
    const isFav = favorites.includes(r.key)
    return (
      <Box onClick={() => openReport(r.key)}
        style={{
          background: '#fff', borderRadius: 14, border: '1px solid #ebebeb',
          padding: small ? 12 : 16, cursor: 'pointer', position: 'relative',
          display: 'flex', flexDirection: 'column', gap: 8, transition: 'all .15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0d9488'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,.06)' }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ebebeb'; e.currentTarget.style.boxShadow = 'none' }}
      >
        <Box onClick={(e) => toggleFavorite(r.key, e)}
          style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', color: isFav ? '#f59e0b' : '#d1d5db' }}>
          <IconStarFilled size={15} style={{ display: isFav ? 'block' : 'none' }} />
          <IconStar size={15} style={{ display: isFav ? 'none' : 'block' }} />
        </Box>
        <Box style={{ width: small ? 34 : 40, height: small ? 34 : 40, borderRadius: 10, background: '#f0fdfa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <r.icon size={small ? 18 : 20} color="#0d9488" />
        </Box>
        <Text fw={600} size={small ? 'xs' : 'sm'}>{t(r.labelKey)}</Text>
        {!small && <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>{t(r.descKey)}</Text>}
      </Box>
    )
  }

  return (
    <Box mb="lg">
      {/* Search + category filter */}
      <Group gap="sm" mb="lg" wrap="wrap">
        <TextInput
          placeholder={t('reports.searchPlaceholder', 'Search reports...')}
          leftSection={<IconSearch size={14} />}
          value={hubSearch}
          onChange={(e) => setHubSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Group gap={6}>
          <Badge component="button" onClick={() => setHubCategory(null)}
            variant={hubCategory === null ? 'filled' : 'light'} color="dark" size="lg"
            style={{ cursor: 'pointer', border: 'none' }}>
            {t('reports.catAll', 'All')}
          </Badge>
          {REPORT_CATEGORIES.map(c => (
            <Badge key={c.key} component="button" onClick={() => setHubCategory(c.key)}
              variant={hubCategory === c.key ? 'filled' : 'light'}
              style={{ cursor: 'pointer', border: 'none', background: hubCategory === c.key ? c.color : undefined, color: hubCategory === c.key ? '#fff' : c.color }}
              size="lg">
              {t(c.labelKey)}
            </Badge>
          ))}
        </Group>
      </Group>

      {/* Favorites */}
      {favoriteMeta.length > 0 && !hubSearch && !hubCategory && (
        <Box mb="lg">
          <Group gap={6} mb={8}>
            <IconStarFilled size={14} color="#f59e0b" />
            <Text size="sm" fw={600}>{t('reports.favorites', 'Favorites')}</Text>
          </Group>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {favoriteMeta.map((r: any) => <ReportCard key={r.key} r={r} small />)}
          </SimpleGrid>
        </Box>
      )}

      {/* Recently used */}
      {recentMeta.length > 0 && !hubSearch && !hubCategory && (
        <Box mb="lg">
          <Group gap={6} mb={8}>
            <IconClock size={14} color="#64748b" />
            <Text size="sm" fw={600}>{t('reports.recentlyUsed', 'Recently Used')}</Text>
          </Group>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {recentMeta.map((r: any) => <ReportCard key={r.key} r={r} small />)}
          </SimpleGrid>
        </Box>
      )}

      {/* All reports, grouped by category (or flat if searching/filtering) */}
      {hubSearch || hubCategory ? (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
          {hubFilteredReports.map((r: any) => <ReportCard key={r.key} r={r} />)}
        </SimpleGrid>
      ) : (
        REPORT_CATEGORIES.map(cat => {
          const items = REPORT_META.filter(r => r.category === cat.key)
          return (
            <Box key={cat.key} mb="lg">
              <Group gap={6} mb={8}>
                <cat.icon size={14} color={cat.color} />
                <Text size="sm" fw={600}>{t(cat.labelKey)}</Text>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
                {items.map(r => <ReportCard key={r.key} r={r} />)}
              </SimpleGrid>
            </Box>
          )
        })
      )}

      {(hubSearch || hubCategory) && hubFilteredReports.length === 0 && (
        <Text size="sm" c="dimmed" ta="center" py="xl">{t('reports.noResults', 'No reports match')}</Text>
      )}
    </Box>
  )
}

export default function ReportsPage() {
  const { t } = useTranslation()
  const { companyLogoUrl, companyName } = useAuth()
  const { isSmall, isMobile } = useBreakpoint()

  // ── Report Hub navigation ──────────────────────────────────────────────
  const [activeReport, setActiveReport] = useState<string | null>(null) // null = แสดงหน้า Hub
  const [hubSearch, setHubSearch] = useState('')
  const [hubCategory, setHubCategory] = useState<string | null>(null)
  const [recents, setRecents] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]') } catch { return [] }
  })
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) ?? '[]') } catch { return [] }
  })

  const openReport = (key: string) => {
    setActiveReport(key)
    setRecents(prev => {
      const next = [key, ...prev.filter(k => k !== key)].slice(0, MAX_RECENTS)
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
      return next
    })
  }

  const toggleFavorite = (key: string, e?: { stopPropagation: () => void }) => {
    e?.stopPropagation()
    setFavorites(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next))
      return next
    })
  }

  const hubFilteredReports = REPORT_META.filter(r => {
    if (hubCategory && r.category !== hubCategory) return false
    if (hubSearch.trim()) {
      const q = hubSearch.trim().toLowerCase()
      const label = t(r.labelKey).toLowerCase()
      if (!label.includes(q)) return false
    }
    return true
  })

  // Filters
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [departments, setDepartments] = useState<any[]>([])
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo]   = useState<string>('')
  const [catFilter, setCatFilter]             = useState<string | null>(null)
  const [subCatFilter, setSubCatFilter]       = useState<string | null>(null)
  const [assetStatusFilter, setAssetStatusFilter]     = useState<string | null>(null)
  const [assetLocationFilter, setAssetLocationFilter] = useState<string | null>(null)
  const [assetDeptFilter, setAssetDeptFilter]         = useState<string | null>(null)

  // Raw data
  const [dashboard, setDashboard]   = useState<any>(null)
  const [assetList, setAssetList]   = useState<any[]>([])
  const [repairs, setRepairs]       = useState<any[]>([])
  const [borrows, setBorrows]       = useState<any[]>([])
  const [handovers, setHandovers]   = useState<any[]>([])
  const [transfers, setTransfers]   = useState<any[]>([])
  const [invItems, setInvItems]     = useState<any[]>([])
  const [invSummary, setInvSummary] = useState<any>(null)
  const [licenses, setLicenses]     = useState<any[]>([])
  const [licSummary, setLicSummary] = useState<any>(null)
  const [checkSessions, setCheckSessions] = useState<any[]>([])
  const [depr, setDepr]             = useState<any>(null)
  const [loading, setLoading]       = useState(true)

  // Depreciation controls
  const [deprMethod, setDeprMethod]     = useState('STRAIGHT_LINE')
  const [deprLife, setDeprLife]         = useState(5)

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [dash, assets, reps, bows, hands, trans, invI, invS, lics, licS, checks, depts] = await Promise.all([
        getDashboard(), getAssetListReport(), getRepairJobs(),
        getAllBorrows(), getHandovers(), getTransfers(),
        getInventoryItems(), getInventorySummary(),
        getLicenses(), getLicenseSummary(),
        getCheckSessions(), getDepartments(),
      ])
      setDashboard(dash.data)
      setAssetList(assets.data ?? [])
      setRepairs(reps.data ?? [])
      setBorrows(bows.data ?? [])
      setHandovers(hands.data ?? [])
      setTransfers(trans.data ?? [])
      setInvItems(invI.data ?? [])
      setInvSummary(invS.data)
      setLicenses(lics.data ?? [])
      setLicSummary(licS.data)
      setCheckSessions(checks.data ?? [])
      setDepartments((depts.data ?? []).map((d: any) => ({ value: d.code, label: d.name })))
    } catch (e) { console.error(e) }
    finally { setLoading(false) }

  }, [])

  const loadDepr = useCallback(async () => {
    try {
      const r = await getDepreciationReport(deprMethod, deprLife)
      setDepr(r.data)
    } catch {}
  }, [deprMethod, deprLife])

  useEffect(() => { loadAll() }, [loadAll])
  useEffect(() => { loadDepr() }, [loadDepr])

  // ── Derived / filtered data ────────────────────────────────────────────
  const fa = deptFilter ? assetList.filter(a => a.departmentCode === deptFilter) : assetList

  // Unique category and subCategory lists derived from the full (dept-filtered) asset list
  const categoryOptions = Array.from(new Set(fa.map((a: any) => a.category).filter(Boolean)))
    .sort()
    .map(c => ({ value: c, label: c }))

  // Sub-cat options depend on selected category
  const subCategoryOptions = catFilter
    ? Array.from(new Set(
        fa.filter((a: any) => a.category === catFilter)
          .map((a: any) => a.subCategory)
          .filter(Boolean)
      )).sort().map(s => ({ value: s, label: s }))
    : []

  const locationOptions = Array.from(new Set(fa.map((a: any) => a.location).filter(Boolean)))
    .sort()
    .map(l => ({ value: l, label: l }))

  const statusOptions = [
    { value: 'AVAILABLE', label: 'Available' },
    { value: 'ASSIGNED',  label: 'Assigned' },
    { value: 'BORROWED',  label: 'Borrowed' },
    { value: 'REPAIR',    label: 'In Repair' },
    { value: 'DAMAGED',   label: 'Damaged' },
    { value: 'LOST',      label: 'Lost' },
    { value: 'WRITE_OFF', label: 'Write Off' },
  ]

  const assetDeptOptions = Array.from(new Set(fa.map((a: any) => a.departmentCode).filter(Boolean)))
    .sort()
    .map(d => {
      const dept = departments.find((x: any) => x.value === d)
      return { value: d, label: dept?.label ?? d }
    })

  const filteredAssets = fa.filter(a => {
    if (catFilter          && a.category       !== catFilter)          return false
    if (subCatFilter       && a.subCategory    !== subCatFilter)       return false
    if (assetStatusFilter  && a.status         !== assetStatusFilter)  return false
    if (assetLocationFilter && a.location      !== assetLocationFilter) return false
    if (assetDeptFilter    && a.departmentCode !== assetDeptFilter)    return false
    if (search) {
      const q = search.toLowerCase()
      if (
        !a.assetCode?.toLowerCase().includes(q) &&
        !a.name?.toLowerCase().includes(q) &&
        !a.brand?.toLowerCase().includes(q) &&
        !a.model?.toLowerCase().includes(q) &&
        !a.category?.toLowerCase().includes(q)
      ) return false
    }
    return true
  })

  // ── Date range helper ──────────────────────────────────────────────────
  const inPeriod = (dateStr: string | undefined | null): boolean => {
    if (!dateFrom && !dateTo) return true
    if (!dateStr) return false
    const d = dateStr.slice(0, 10)  // normalise to YYYY-MM-DD
    if (dateFrom && d < dateFrom) return false
    if (dateTo   && d > dateTo)   return false
    return true
  }

  const periodLabel = dateFrom || dateTo
    ? `${dateFrom ? new Date(dateFrom).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : 'Start'} → ${dateTo ? new Date(dateTo).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : 'Today'}`
    : null

  const filteredRepairs = (deptFilter
    ? repairs.filter(j => fa.some(a => a.assetCode === j.assetCode))
    : repairs
  ).filter(j => inPeriod(j.createdAt))

  const filteredBorrows = borrows.filter(b => inPeriod(b.borrowDate || b.createdAt))

  const filteredHandovers = (deptFilter
    ? handovers.filter(h => (h.assets ?? []).some((a: any) => fa.some(fa2 => fa2.assetCode === a.assetCode)))
    : handovers
  ).filter(h => inPeriod(h.handoverDate || h.createdAt))
  const filteredTransfers = (deptFilter
    ? transfers.filter(t2 => t2.fromDeptCode === deptFilter || t2.toDeptCode === deptFilter)
    : transfers
  ).filter(t2 => inPeriod(t2.createdAt))
  const filteredInv  = deptFilter ? invItems.filter(i => i.departmentCode === deptFilter) : invItems
  const filteredLics = (deptFilter ? licenses.filter(l => l.departmentCode === deptFilter) : licenses)
    .filter(l => {
      // License active during period = startDate <= dateTo AND (expiryDate >= dateFrom OR no expiry)
      if (!dateFrom && !dateTo) return true
      const start  = l.startDate?.slice(0, 10)
      const expiry = l.expiryDate?.slice(0, 10)
      if (dateTo   && start  && start  > dateTo)   return false
      if (dateFrom && expiry && expiry < dateFrom)  return false
      return true
    })

  // Asset breakdowns
  const byStatus   = fa.reduce((acc: any, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc }, {})
  const byCategory = fa.reduce((acc: any, a) => { const k = a.category || 'Uncategorised'; acc[k] = (acc[k] || 0) + 1; return acc }, {})
  const byDept     = fa.reduce((acc: any, a) => { const k = a.departmentCode || '—'; acc[k] = (acc[k] || 0) + 1; return acc }, {})
  const byModel    = fa.reduce((acc: any, a) => { const k = `${a.brand ?? ''} ${a.model ?? ''}`.trim() || '—'; acc[k] = (acc[k] || 0) + 1; return acc }, {})

  // Repair stats
  const repairOpen      = filteredRepairs.filter(j => j.status === 'OPEN').length
  const repairDone      = filteredRepairs.filter(j => j.status === 'COMPLETED').length
  const repairDamaged   = filteredRepairs.filter(j => j.status === 'WRITTEN_OFF').length
  const repairReplaced  = filteredRepairs.filter(j => j.status === 'REPLACED').length
  const repairCost      = filteredRepairs.reduce((s: number, j: any) => s + Number(j.estimatedCost || 0), 0)
  const repairActualCost = filteredRepairs.reduce((s: number, j: any) => s + Number(j.actualCost || 0), 0)

  // ── สำหรับกราฟ: ค่าซ่อมรายเดือน (6 เดือนล่าสุด) + จำนวนตามประเภทงานซ่อม ──
  const repairByMonth: Record<string, number> = {}
  const now2 = new Date()
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now2.getFullYear(), now2.getMonth() - i, 1)
    repairByMonth[d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })] = 0
  }
  filteredRepairs.forEach((j: any) => {
    if (!j.createdAt) return
    const d = new Date(j.createdAt)
    const key = d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
    if (key in repairByMonth) repairByMonth[key] += Number(j.estimatedCost || 0)
  })
  const repairByType = filteredRepairs.reduce((acc: any, j: any) => { acc[j.type] = (acc[j.type] || 0) + 1; return acc }, {})

  // Borrow stats
  const borrowPending = filteredBorrows.filter(b => b.status === 'PENDING').length
  const borrowActive  = filteredBorrows.filter(b => ['APPROVED','PARTIALLY_RETURNED'].includes(b.status)).length
  const borrowOverdue = filteredBorrows.filter(b => b.status === 'OVERDUE').length
  const borrowByStatus = filteredBorrows.reduce((acc: any, b: any) => { acc[b.status] = (acc[b.status] || 0) + 1; return acc }, {})
  const borrowByPerson = filteredBorrows.reduce((acc: any, b: any) => { const k = b.borrowerName || 'Unknown'; acc[k] = (acc[k] || 0) + 1; return acc }, {})

  // Handover stats
  const hoApproved = filteredHandovers.filter(h => h.status === 'RECEIVED').length
  const hoDraft    = filteredHandovers.filter(h => h.status === 'DRAFT').length
  const handoverByStatus = filteredHandovers.reduce((acc: any, h: any) => { acc[h.status] = (acc[h.status] || 0) + 1; return acc }, {})
  const handoverByDept = filteredHandovers.reduce((acc: any, h: any) => { const k = h.departmentCode || 'Unknown'; acc[k] = (acc[k] || 0) + 1; return acc }, {})

  // Transfer stats
  const trPending  = filteredTransfers.filter(t2 => t2.status === 'PENDING').length
  const trApproved = filteredTransfers.filter(t2 => t2.status === 'APPROVED').length
  const transferByStatus = filteredTransfers.reduce((acc: any, t2: any) => { acc[t2.status] = (acc[t2.status] || 0) + 1; return acc }, {})
  const transferByFromDept = filteredTransfers.reduce((acc: any, t2: any) => { const k = t2.fromDeptCode || 'Unknown'; acc[k] = (acc[k] || 0) + 1; return acc }, {})

  // Inventory stats
  const invLow  = filteredInv.filter(i => (i.stockQty ?? 0) > 0 && (i.stockQty ?? 0) <= (i.minStock ?? 0)).length
  const invOut  = filteredInv.filter(i => (i.stockQty ?? 0) === 0).length
  const invVal  = filteredInv.reduce((s, i) => s + (i.stockQty ?? 0) * (i.unitCost ?? 0), 0)
  const invNormal = filteredInv.length - invLow - invOut
  const invByStockStatus = { NORMAL: invNormal, LOW: invLow, OUT: invOut }
  const invByCategoryValue = filteredInv.reduce((acc: any, i: any) => {
    const k = i.category || 'Uncategorised'
    acc[k] = (acc[k] || 0) + (i.stockQty ?? 0) * (i.unitCost ?? 0)
    return acc
  }, {})

  // License stats
  const licActive   = filteredLics.filter(l => l.status === 'ACTIVE').length
  const licExpiring = filteredLics.filter(l => l.status === 'EXPIRING').length
  const licExpired  = filteredLics.filter(l => l.status === 'EXPIRED').length

  // Check session stats
  const csCompleted = checkSessions.filter(s => s.status === 'COMPLETED').length

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const deptLabel = deptFilter ? (departments.find(d => d.value === deptFilter)?.label ?? deptFilter) : 'All Departments'

  // ── Print summary HTML ─────────────────────────────────────────────────
  const summaryHtml = `
    <style>
      h1 { font-size: 18px; font-weight: 700; color: #0f172a; margin-bottom: 4px; font-family: 'Sarabun', sans-serif; }
      .meta { font-size: 11px; color: #64748b; margin-bottom: 16px; }
      h2 { font-size: 13px; font-weight: 700; margin: 16px 0 8px; color: #0f172a;
        border-left: 3px solid #0d9488; padding-left: 8px; font-family: 'Sarabun', sans-serif; }
      .cards { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
      .card { border: 1px solid #e2e8f0; border-radius: 7px; padding: 10px 14px; min-width: 100px; }
      .card-label { font-size: 9px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px; }
      .card-value { font-size: 20px; font-weight: 700; font-family: 'Inter',sans-serif; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 12px; }
      thead tr { background: #f1f5f9; }
      th { text-align: left; padding: 6px 8px; font-weight: 700; color: #475569;
        font-size: 9px; text-transform: uppercase; letter-spacing: 0.4px; border: 1px solid #e2e8f0; }
      td { padding: 5px 8px; border: 1px solid #e2e8f0; vertical-align: top; }
      .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 9px; font-weight: 700; }
      .t { background: #f0fdf4; color: #0d9488; } .o { background: #fff7ed; color: #c2410c; }
      .r { background: #fef2f2; color: #dc2626; } .b { background: #eff6ff; color: #1d4ed8; }
      .g { background: #f8fafc; color: #64748b; } .v { background: #f5f3ff; color: #7c3aed; }
      body, * { font-family: 'Sarabun', 'Inter', sans-serif; }
    </style>
    <h1>AssetOS — System Summary Report</h1>
    <p class="meta">Date: ${today} | Department: ${deptLabel}</p>

    <h2>📦 Assets Overview</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("common.total","Total")}</div><div class="card-value" style="color:#0f172a">${fa.length}</div></div>
      ${Object.entries(byStatus).map(([s,c]:any) => `<div class="card"><div class="card-label">${s}</div><div class="card-value" style="color:#0d9488">${c}</div></div>`).join('')}
    </div>

    <h2>📊 By Category</h2>
    <table>
      <thead><tr><th>${t("common.category","Category")}</th><th>${t("common.count","Count")}</th><th>%</th></tr></thead>
      <tbody>${Object.entries(byCategory).sort(([,a]:any,[,b]:any)=>b-a).map(([cat,cnt]:any)=>`<tr><td>${cat}</td><td>${cnt}</td><td>${fa.length?((cnt/fa.length)*100).toFixed(1):'0'}%</td></tr>`).join('')}</tbody>
    </table>

    <h2>🔧 Repair</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("reports.totalJobs","Total Jobs")}</div><div class="card-value" style="color:#0f172a">${filteredRepairs.length}</div></div>
      <div class="card"><div class="card-label">${t("reports.open","Open")}</div><div class="card-value" style="color:#f59e0b">${repairOpen}</div></div>
      <div class="card"><div class="card-label">${t("common.completed","Completed")}</div><div class="card-value" style="color:#0d9488">${repairDone}</div></div>
      <div class="card"><div class="card-label">${t("reports.estCost","Est. Cost")}</div><div class="card-value" style="color:#6366f1">฿${repairCost.toLocaleString()}</div></div>
    </div>
    <table>
      <thead><tr><th>${t("reports.assetCode","Asset Code")}</th><th>${t("reports.problem","Problem")}</th><th>${t("common.type","Type")}</th><th>${t("common.status","Status")}</th><th>${t("reports.estCost","Est. Cost")}</th><th>${t("common.date","Date")}</th></tr></thead>
      <tbody>${filteredRepairs.slice(0,25).map((j:any)=>{const bm=[j.asset?.brand,j.asset?.model].filter(Boolean).join(' ');return `<tr><td>${j.assetCode??'—'}${bm?`<br/><span style="color:#94a3b8;font-size:11px">Brand / Model: ${bm}</span>`:''}</td><td>${j.problem??'—'}</td><td>${j.type??'—'}</td><td><span class="badge ${j.status==='OPEN'?'o':j.status==='COMPLETED'?'t':'g'}">${j.status}</span></td><td>${j.estimatedCost?'฿'+Number(j.estimatedCost).toLocaleString():'—'}</td><td>${j.createdAt?.slice(0,10)??'—'}</td></tr>`}).join('')}${filteredRepairs.length>25?`<tr><td colspan="6" style="color:#94a3b8;font-style:italic">...and ${filteredRepairs.length-25} more</td></tr>`:''}</tbody>
    </table>

    <div class="page-break"></div>

    <h2>📋 Borrow</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("common.total","Total")}</div><div class="card-value">${filteredBorrows.length}</div></div>
      <div class="card"><div class="card-label">${t("common.pending","Pending")}</div><div class="card-value" style="color:#f59e0b">${borrowPending}</div></div>
      <div class="card"><div class="card-label">${t("common.active","Active")}</div><div class="card-value" style="color:#3b82f6">${borrowActive}</div></div>
      <div class="card"><div class="card-label">${t("common.overdue","Overdue")}</div><div class="card-value" style="color:#ef4444">${borrowOverdue}</div></div>
    </div>

    <h2>🤝 Handover</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("common.total","Total")}</div><div class="card-value">${filteredHandovers.length}</div></div>
      <div class="card"><div class="card-label">${t("common.received","Received")}</div><div class="card-value" style="color:#0d9488">${hoApproved}</div></div>
      <div class="card"><div class="card-label">${t("common.pending","Pending")}</div><div class="card-value" style="color:#f59e0b">${hoDraft}</div></div>
    </div>

    <h2>🔄 Transfer</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("common.total","Total")}</div><div class="card-value">${filteredTransfers.length}</div></div>
      <div class="card"><div class="card-label">${t("common.pending","Pending")}</div><div class="card-value" style="color:#f59e0b">${trPending}</div></div>
      <div class="card"><div class="card-label">${t("common.received","Received")}</div><div class="card-value" style="color:#0d9488">${trApproved}</div></div>
    </div>

    <h2>📦 Inventory Consumables</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("common.items","Items")}</div><div class="card-value">${filteredInv.length}</div></div>
      <div class="card"><div class="card-label">${t("reports.lowStock","Low Stock")}</div><div class="card-value" style="color:#f59e0b">${invLow}</div></div>
      <div class="card"><div class="card-label">${t("reports.outOfStock","Out of Stock")}</div><div class="card-value" style="color:#ef4444">${invOut}</div></div>
      <div class="card"><div class="card-label">${t("reports.totalValue","Total Value")}</div><div class="card-value" style="color:#6366f1">฿${invVal.toLocaleString()}</div></div>
    </div>

    <h2>🔑 Licenses</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("common.total","Total")}</div><div class="card-value">${filteredLics.length}</div></div>
      <div class="card"><div class="card-label">${t("common.active","Active")}</div><div class="card-value" style="color:#0d9488">${licActive}</div></div>
      <div class="card"><div class="card-label">${t("reports.expiring","Expiring")}</div><div class="card-value" style="color:#f59e0b">${licExpiring}</div></div>
      <div class="card"><div class="card-label">${t("reports.expired","Expired")}</div><div class="card-value" style="color:#ef4444">${licExpired}</div></div>
    </div>

    <h2>✅ Asset Checking (COMSET)</h2>
    <div class="cards">
      <div class="card"><div class="card-label">${t("reports.sessions","Sessions")}</div><div class="card-value">${checkSessions.length}</div></div>
      <div class="card"><div class="card-label">${t("common.completed","Completed")}</div><div class="card-value" style="color:#0d9488">${csCompleted}</div></div>
    </div>
  `

  if (loading) return <Center p="xl"><Loader color="teal" /></Center>

  return (
    <Box p={isSmall ? 16 : 32}>
      {/* Header */}
      <Group justify="space-between" mb="xl" align="flex-start" wrap="wrap" gap="sm">
        <Box>
          <Text style={{ fontFamily: 'Inter', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>{t("reports.title","Reports")}</Text>
          <Text size="sm" c="dimmed">{t("reports.subtitle","System overview and detailed reports for all modules")}</Text>
        </Box>
        <Group gap="sm" wrap="wrap">
          <Select placeholder={t("reports.allDept","All Departments")} value={deptFilter} onChange={setDeptFilter}
            data={departments} clearable size="sm" leftSection={<IconFilter size={14} />} style={{ width: 200 }} />
          <TextInput placeholder={t("reports.searchAssets","Search assets...")} value={search} onChange={e => setSearch(e.target.value)}
            leftSection={<IconSearch size={14} />} size="sm" style={{ width: 180 }}
            rightSection={search ? <Box style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1 }} onClick={() => setSearch('')}>×</Box> : null} />
          <Button leftSection={<IconPrinter size={16} />} style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }} size="sm"
            onClick={() => {
              const el = document.getElementById('_summary_print_')
              if (el) { el.innerHTML = summaryHtml; printSection('_summary_print_', 'System Summary Report — AssetOS', undefined, companyLogoUrl, companyName, t) }
            }}>
            {t("reports.printSummary","Print Summary")}
          </Button>
        </Group>
      </Group>

      {/* ── Period Filter Bar ───────────────────────────────────────────── */}
      <Paper withBorder radius="md" p="sm" mb="lg"
        style={{ borderColor: (dateFrom || dateTo) ? '#6366f1' : '#e2e8f0', background: (dateFrom || dateTo) ? '#f5f3ff' : undefined }}>
        <Group gap={12} align="flex-end" wrap="wrap">
          <IconCalendar size={18} color={(dateFrom || dateTo) ? '#6366f1' : '#94a3b8'} style={{ flexShrink: 0, marginBottom: 2 }} />
          <Text size="sm" fw={500} c={(dateFrom || dateTo) ? 'violet' : 'dimmed'} style={{ flexShrink: 0 }}>
            {t("reports.periodFilter","Report Period")}:
          </Text>
          <SmartDateInput
            label={t("reports.from","From")}
            placeholder={t("reports.startDate","Start date")}
            value={dateFrom}
            onChange={v => setDateFrom(v)}
            clearable
          />
          <Text size="sm" c="dimmed" style={{ flexShrink: 0, marginBottom: 2 }}>—</Text>
          <SmartDateInput
            label="To"
            placeholder={t("reports.endDate","End date")}
            value={dateTo}
            onChange={v => setDateTo(v)}
            clearable
          />
          {(dateFrom || dateTo) && (
            <>
              <Badge color="violet" variant="light" size="md" style={{ flexShrink: 0 }}>
                📅 {periodLabel}
              </Badge>
              <Button size="xs" variant="subtle" color="gray" leftSection={<IconX size={12} />}
                onClick={() => { setDateFrom(''); setDateTo('') }}>
                {t("reports.clearPeriod","Clear Period")}
              </Button>
            </>
          )}
          {!dateFrom && !dateTo && (
            <Text size="xs" c="dimmed" fs="italic">{t("reports.allTime","Showing all time — select dates to filter by period")}</Text>
          )}
          <Group gap={6} ml="auto" style={{ flexShrink: 0 }}>
            <Button size="xs" variant="outline" color="gray"
              onClick={() => {
                const now = new Date()
                const today = now.toISOString().slice(0, 10)
                setDateFrom(today); setDateTo(today)
              }}>{t("reports.today","Today")}</Button>
            <Button size="xs" variant="outline" color="gray"
              onClick={() => {
                const now = new Date()
                const day = now.getDay() // 0=Sun
                const diffToMon = day === 0 ? 6 : day - 1
                const weekStart = new Date(now); weekStart.setDate(now.getDate() - diffToMon)
                const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6)
                setDateFrom(weekStart.toISOString().slice(0, 10))
                setDateTo(weekEnd.toISOString().slice(0, 10))
              }}>{t("reports.thisWeek","This Week")}</Button>
            <Button size="xs" variant="outline" color="gray"
              onClick={() => {
                const now = new Date()
                const y = now.getFullYear()
                const q = Math.floor(now.getMonth() / 3)
                const qStart = new Date(y, q * 3, 1)
                const qEnd   = new Date(y, q * 3 + 3, 0)
                setDateFrom(qStart.toISOString().slice(0, 10))
                setDateTo(qEnd.toISOString().slice(0, 10))
              }}>{t("reports.thisQuarter","This Quarter")}</Button>
            <Button size="xs" variant="outline" color="gray"
              onClick={() => {
                const now = new Date()
                setDateFrom(`${now.getFullYear()}-01-01`)
                setDateTo(`${now.getFullYear()}-12-31`)
              }}>{t("reports.thisYear","This Year")}</Button>
            <Button size="xs" variant="outline" color="gray"
              onClick={() => {
                const now = new Date()
                const y = now.getFullYear()
                const q = Math.floor(now.getMonth() / 3)
                const prevQ = q === 0 ? 3 : q - 1
                const prevY = q === 0 ? y - 1 : y
                const qStart = new Date(prevY, prevQ * 3, 1)
                const qEnd   = new Date(prevY, prevQ * 3 + 3, 0)
                setDateFrom(qStart.toISOString().slice(0, 10))
                setDateTo(qEnd.toISOString().slice(0, 10))
              }}>{t("reports.lastQuarter","Last Quarter")}</Button>
          </Group>
        </Group>
      </Paper>

      <div id="_summary_print_" style={{ position: 'fixed', left: -9999, top: 0, width: 900 }} />

      {activeReport === null ? (
        <ReportHub
          t={t}
          hubSearch={hubSearch} setHubSearch={setHubSearch}
          hubCategory={hubCategory} setHubCategory={setHubCategory}
          hubFilteredReports={hubFilteredReports}
          recents={recents} favorites={favorites}
          openReport={openReport} toggleFavorite={toggleFavorite}
        />
      ) : (
        <Group gap={6} mb="md" style={{ cursor: 'pointer' }} onClick={() => setActiveReport(null)}>
          <IconArrowsTransferDown size={14} style={{ transform: 'rotate(90deg)' }} />
          <Text size="sm" fw={600} c="teal">{t('reports.backToHub', '← All Reports')}</Text>
        </Group>
      )}

      <Tabs value={activeReport} onChange={setActiveReport} keepMounted={false}>
        <Tabs.List mb="lg" style={{ overflowX: 'auto', flexWrap: 'nowrap', display: activeReport === null ? 'none' : 'flex' }}>
          <Tabs.Tab value="summary"       leftSection={<IconLayoutDashboard size={13} />}>{t("common.summary","📊 Summary")}</Tabs.Tab>
          <Tabs.Tab value="assets"        leftSection={<IconFileText size={13} />}>{t("reports.assets","Assets")}</Tabs.Tab>
          <Tabs.Tab value="breakdown"     leftSection={<IconChartBar size={13} />}>{t("reports.breakdown","Breakdown")}</Tabs.Tab>
          <Tabs.Tab value="repair"        leftSection={<IconTool size={13} />}>{t("reports.repair","Repair")}</Tabs.Tab>
          <Tabs.Tab value="workorders"    leftSection={<IconTicket size={13} />}>{t("reports.workorders","Work/HK Orders")}</Tabs.Tab>
          <Tabs.Tab value="borrow"        leftSection={<IconArrowsExchange size={13} />}>{t("reports.borrow","Borrow")}</Tabs.Tab>
          <Tabs.Tab value="handover"      leftSection={<IconFileText size={13} />}>{t("reports.handover","Handover")}</Tabs.Tab>
          <Tabs.Tab value="transfer"      leftSection={<IconArrowsTransferDown size={13} />}>{t("reports.transfer","Transfer")}</Tabs.Tab>
          <Tabs.Tab value="inventory"     leftSection={<IconPackage size={13} />}>{t("nav.inventory","Inventory")}</Tabs.Tab>
          <Tabs.Tab value="license"       leftSection={<IconKey size={13} />}>{t("nav.licenses","Licenses")}</Tabs.Tab>
          <Tabs.Tab value="checking"      leftSection={<IconClipboardCheck size={13} />}>{t("nav.assetChecking","Checking")}</Tabs.Tab>
          <Tabs.Tab value="depreciation"  leftSection={<IconChartBar size={13} />}>{t("reports.depreciation","Depreciation")}</Tabs.Tab>
        </Tabs.List>

        {/* ─── SUMMARY ─────────────────────────────────────────────── */}
        <Tabs.Panel value="summary">
          {/* ── Headline visuals — ภาพรวมก่อนตัวเลขละเอียด ── */}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.assetsByStatusChart", "Assets by Status")}</Text>
              <StatusDonut
                data={byStatus} total={fa.length} colorMap={STATUS_HEX} centerLabel={t("reports.totalAssets", "Total Assets")}
                labelMap={{
                  AVAILABLE: t('assetStatus.available', 'Available'), ASSIGNED: t('assetStatus.assigned', 'Assigned'),
                  BORROWED: t('assetStatus.borrowed', 'Borrowed'), INSTALLED: t('assetStatus.installed', 'Installed'),
                  REPAIR: t('assetStatus.repair', 'In Repair'), DAMAGED: t('assetStatus.damaged', 'Damaged'),
                  LOST: t('assetStatus.lost', 'Lost'), WRITE_OFF: t('assetStatus.writeOff', 'Written Off'),
                }}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.topCategoriesChart", "Top Categories")}</Text>
              <TopBarChart data={byCategory} limit={7} />
            </Paper>
          </SimpleGrid>

          <SecHeader icon={<IconFileText size={15} />} title={t("reports.secAssets","Assets")} />
          <SimpleGrid cols={{ base: 2, md: 5 }} mb="md">
            <KPI label={t("reports.totalAssets","Total Assets")} value={fa.length} />
            <KPI label={t("reports.available","Available")}    value={byStatus['AVAILABLE'] ?? 0}  color="#0d9488" />
            <KPI label={t("reports.inUse","In Use")}       value={(byStatus['ASSIGNED']??0)+(byStatus['BORROWED']??0)} color="#3b82f6"
              sub={`${t('reports.assigned','Assigned')} ${byStatus['ASSIGNED']??0} / ${t('reports.borrowed','Borrowed')} ${byStatus['BORROWED']??0}`} />
            <KPI label={t("reports.inRepairKpi","In Repair")}    value={byStatus['REPAIR'] ?? 0}     color="#f59e0b" />
            <KPI label={t("reports.issues","Issues")}       value={(byStatus['DAMAGED']??0)+(byStatus['LOST']??0)+(byStatus['WRITE_OFF']??0)} color="#ef4444"
              sub={`${t('reports.damaged','Damaged')} ${byStatus['DAMAGED']??0} / ${t('reports.lost','Lost')} ${byStatus['LOST']??0} / ${t('reports.writeOff','WO')} ${byStatus['WRITE_OFF']??0}`} />
          </SimpleGrid>

          <SecHeader icon={<IconTool size={15} />} title={t("reports.secRepair","Repair")} />
          <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
            <KPI label={t("reports.totalJobs","Total Jobs")}    value={filteredRepairs.length} />
            <KPI label={t("reports.openJobs","Open")}          value={repairOpen}  color="#f59e0b" />
            <KPI label={t("reports.completedJobs","Completed")}     value={repairDone}  color="#0d9488" />
            <KPI label={t("reports.estimatedCost","Est. Cost")}     value={`฿${repairCost.toLocaleString()}`} color="#6366f1" />
          </SimpleGrid>

          <SecHeader icon={<IconArrowsExchange size={15} />} title={t("reports.secBHT","Borrow & Handover & Transfer")} />
          <SimpleGrid cols={{ base: 2, md: 6 }} mb="md">
            <KPI label={t("reports.borrows","Borrows")}       value={filteredBorrows.length} />
            <KPI label={t("reports.borrowActive","Borrow Active")} value={borrowActive}  color="#3b82f6" />
            <KPI label={t("common.overdue","Overdue")}       value={borrowOverdue} color="#ef4444" />
            <KPI label={t("reports.handovers","Handovers")}     value={filteredHandovers.length} />
            <KPI label={t("reports.hoApproved","HO Approved")}   value={hoApproved}   color="#0d9488" />
            <KPI label={t("reports.transfers","Transfers")}     value={filteredTransfers.length} />
          </SimpleGrid>

          <SecHeader icon={<IconPackage size={15} />} title={t("reports.secInventory","Inventory Consumables")} />
          <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
            <KPI label={t("common.items","Items")}         value={filteredInv.length} />
            <KPI label={t("reports.lowStock","Low Stock")}     value={invLow}   color="#f59e0b" />
            <KPI label={t("reports.outOfStock","Out of Stock")}  value={invOut}   color="#ef4444" />
            <KPI label={t("reports.totalValue","Total Value")}   value={`฿${invVal.toLocaleString()}`} color="#6366f1" />
          </SimpleGrid>

          <SecHeader icon={<IconKey size={15} />} title={t("reports.secLicenses","Licenses")} />
          <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
            <KPI label={t("common.total","Total")}         value={filteredLics.length} />
            <KPI label={t("common.active","Active")}        value={licActive}   color="#0d9488" />
            <KPI label={t("reports.expiringSoon","Expiring Soon")} value={licExpiring} color="#f59e0b" />
            <KPI label={t("reports.expired","Expired")}       value={licExpired}  color="#ef4444" />
          </SimpleGrid>

          <SecHeader icon={<IconClipboardCheck size={15} />} title={t("reports.secChecking","Asset Checking (COMSET)")} />
          <SimpleGrid cols={{ base: 2, md: 3 }} mb="md">
            <KPI label={t("reports.sessions","Sessions")}   value={checkSessions.length} />
            <KPI label={t("reports.completedJobs","Completed")}  value={csCompleted} color="#0d9488" />
            <KPI label={t("common.pending","Pending")}    value={checkSessions.length - csCompleted} color="#f59e0b" />
          </SimpleGrid>
        </Tabs.Panel>

        {/* ─── ASSETS ──────────────────────────────────────────────── */}
        <Tabs.Panel value="assets">
          {/* Filter bar */}
          <Group gap={8} mb={6} wrap="wrap" align="flex-end">
            <Select placeholder={t("reports.category","All Categories")} data={categoryOptions}
              value={catFilter} onChange={v => { setCatFilter(v); setSubCatFilter(null) }}
              clearable size="xs" leftSection={<IconFilter size={12} />} style={{ width: 170 }} label={t("reports.category","Category")} />
            {catFilter && subCategoryOptions.length > 0 && (
              <Select placeholder={t("reports.allSubcats","All Sub-cats")} data={subCategoryOptions}
                value={subCatFilter} onChange={setSubCatFilter}
                clearable size="xs" leftSection={<IconFilter size={12} />} style={{ width: 160 }} label={t("reports.subCategory","Sub-category")} />
            )}
            <Select placeholder={t("reports.allStatuses","All Statuses")} data={statusOptions}
              value={assetStatusFilter} onChange={setAssetStatusFilter}
              clearable size="xs" leftSection={<IconFilter size={12} />} style={{ width: 150 }} label={t("reports.status","Status")} />
            <Select placeholder={t("reports.allLocations","All Locations")} data={locationOptions}
              value={assetLocationFilter} onChange={setAssetLocationFilter}
              clearable size="xs" searchable leftSection={<IconFilter size={12} />} style={{ width: 170 }} label={t("reports.location","Location")} />
            <Select placeholder={t("reports.allDept","All Departments")} data={assetDeptOptions}
              value={assetDeptFilter} onChange={setAssetDeptFilter}
              clearable size="xs" searchable leftSection={<IconFilter size={12} />} style={{ width: 190 }} label={t("reports.department","Department")} />
            {(catFilter || subCatFilter || assetStatusFilter || assetLocationFilter || assetDeptFilter) && (
              <Box style={{ paddingBottom: 1 }}>
                <Button size="xs" variant="subtle" color="gray" leftSection={<IconX size={11} />}
                  onClick={() => {
                    setCatFilter(null); setSubCatFilter(null)
                    setAssetStatusFilter(null); setAssetLocationFilter(null); setAssetDeptFilter(null)
                  }}>
                  Clear Filters
                </Button>
              </Box>
            )}
          </Group>
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-assets" title="Asset Report" />
            <CSVBtn label="assets-report.csv" rows={filteredAssets} columns={[
              { key: 'assetCode', label: 'Asset Code' }, { key: 'name', label: 'Name' },
              { key: 'brand', label: 'Brand' }, { key: 'model', label: 'Model' },
              { key: 'category', label: 'Category' }, { key: 'subCategory', label: 'Sub-Category' },
              { key: 'serialNumber', label: 'Serial No.' }, { key: 'departmentCode', label: 'Department' },
              { key: 'status', label: 'Status' }, { key: 'location', label: 'Location' },
              { key: 'owner', label: 'Owner' }, { key: 'purchasePrice', label: 'Purchase Price' },
              { key: 'warrantyEnd', label: 'Warranty End' },
            ]} />
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            Showing {filteredAssets.length} of {fa.length} assets
            {assetDeptFilter ? ` · Dept: ${assetDeptOptions.find(d => d.value === assetDeptFilter)?.label ?? assetDeptFilter}` : ''}
            {catFilter ? ` · Category: ${catFilter}${subCatFilter ? ` / ${subCatFilter}` : ''}` : ''}
            {assetStatusFilter ? ` · Status: ${assetStatusFilter}` : ''}
            {assetLocationFilter ? ` · Location: ${assetLocationFilter}` : ''}
            {search ? ` · Search: "${search}"` : ''}
          </Text>
          <Box id="rpt-assets">
            {isMobile ? (
              filteredAssets.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noAssets","No assets")}</Text></Center>
              ) : (
                <Box>
                  {filteredAssets.map((a: any) => (
                    <ReportCard key={a.id}
                      title={a.assetCode}
                      subtitle={a.name}
                      badge={<Badge size="xs" color={STATUS_COLOR[a.status] ?? 'gray'} variant="light">{a.status}</Badge>}
                      details={[
                        { label: 'Brand', value: a.brand ?? '—' },
                        { label: 'Model', value: a.model ?? '—' },
                        { label: 'Category', value: `${a.category}${a.subCategory ? ` / ${a.subCategory}` : ''}` },
                        { label: 'S/N', value: a.serialNumber ?? '—' },
                        { label: 'Department', value: a.departmentCode || '—' },
                        { label: 'Location', value: a.location ?? '—' },
                        { label: 'Owner', value: a.owner ?? '—' },
                        { label: 'Warranty', value: <Text size="xs" c={a.warrantyEnd && new Date(a.warrantyEnd) < new Date() ? 'red' : 'dimmed'}>{a.warrantyEnd?.slice(0,10) || '—'}</Text> },
                      ]}
                    />
                  ))}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['Code','Name','Brand','Model','Category','S/N','Department','Status','Location','Owner','Warranty'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredAssets.length === 0
                    ? <Table.Tr><Table.Td colSpan={11}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noAssets","No assets")}</Text></Center></Table.Td></Table.Tr>
                    : filteredAssets.map((a: any) => (
                      <Table.Tr key={a.id}>
                        <Table.Td><Text size="xs" fw={600} style={{ fontFamily: 'Inter' }}>{a.assetCode}</Text></Table.Td>
                        <Table.Td><Text size="xs">{a.name}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.brand ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.model ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.category}{a.subCategory ? ` / ${a.subCategory}` : ''}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.serialNumber}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.departmentCode || '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={STATUS_COLOR[a.status] ?? 'gray'} variant="light">{a.status}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.location ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{a.owner ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c={a.warrantyEnd && new Date(a.warrantyEnd) < new Date() ? 'red' : 'dimmed'}>{a.warrantyEnd?.slice(0,10) || '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── BREAKDOWN ───────────────────────────────────────────── */}
        <Tabs.Panel value="breakdown">
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-breakdown" title="Asset Breakdown" />
            <CSVBtn label="breakdown-by-category.csv"
              rows={Object.entries(byCategory).sort(([,a]:any,[,b]:any)=>b-a).map(([k,v])=>({ category: k, count: v }))}
              columns={[{ key: 'category', label: 'Category' }, { key: 'count', label: 'Count' }]} />
          </Group>
          <Box id="rpt-breakdown">
            <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
              {/* By Status */}
              <Paper withBorder radius="md" p="md">
                <Text fw={600} size="sm" mb="md">{t("reports.byStatus","By Status")}</Text>
                <StatusDonut
                  data={byStatus} total={fa.length} colorMap={STATUS_HEX} centerLabel={t("reports.totalAssets", "Total Assets")} height={170}
                  labelMap={{
                    AVAILABLE: t('assetStatus.available', 'Available'), ASSIGNED: t('assetStatus.assigned', 'Assigned'),
                    BORROWED: t('assetStatus.borrowed', 'Borrowed'), INSTALLED: t('assetStatus.installed', 'Installed'),
                    REPAIR: t('assetStatus.repair', 'In Repair'), DAMAGED: t('assetStatus.damaged', 'Damaged'),
                    LOST: t('assetStatus.lost', 'Lost'), WRITE_OFF: t('assetStatus.writeOff', 'Written Off'),
                  }}
                />
              </Paper>

              {/* By Category */}
              <Paper withBorder radius="md" p="md">
                <Text fw={600} size="sm" mb="md">{t("reports.byCategory","By Category")}</Text>
                <TopBarChart data={byCategory} limit={8} height={170} />
              </Paper>

              {/* By Department */}
              <Paper withBorder radius="md" p="md">
                <Text fw={600} size="sm" mb="md">{t("reports.byDepartment","By Department")}</Text>
                <ScrollArea h={220}>
                  <Table>
                    <Table.Thead><Table.Tr>
                      <Table.Th style={thStyle}>{t("common.department","Department")}</Table.Th>
                      <Table.Th style={thStyle}>{t("common.count","Count")}</Table.Th>
                      <Table.Th style={thStyle}>%</Table.Th>
                    </Table.Tr></Table.Thead>
                    <Table.Tbody>
                      {Object.entries(byDept).sort(([,a]:any,[,b]:any)=>b-a).map(([dept,c]:any) => (
                        <Table.Tr key={dept}>
                          <Table.Td><Text size="xs">{dept}</Text></Table.Td>
                          <Table.Td><Text size="xs" fw={600}>{c}</Text></Table.Td>
                          <Table.Td><Text size="xs" c="dimmed">{fa.length ? ((c/fa.length)*100).toFixed(1) : 0}%</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>

              {/* By Model */}
              <Paper withBorder radius="md" p="md">
                <Text fw={600} size="sm" mb="md">{t("reports.byBrandModel","By Brand / Model")}</Text>
                <ScrollArea h={220}>
                  <Table>
                    <Table.Thead><Table.Tr>
                      <Table.Th style={thStyle}>{t("reports.brandModel","Brand / Model")}</Table.Th>
                      <Table.Th style={thStyle}>{t("common.count","Count")}</Table.Th>
                    </Table.Tr></Table.Thead>
                    <Table.Tbody>
                      {Object.entries(byModel).sort(([,a]:any,[,b]:any)=>b-a).map(([model,c]:any) => (
                        <Table.Tr key={model}>
                          <Table.Td><Text size="xs">{model}</Text></Table.Td>
                          <Table.Td><Text size="xs" fw={600}>{c}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>
            </SimpleGrid>
          </Box>
        </Tabs.Panel>

        {/* ─── REPAIR ──────────────────────────────────────────────── */}
        <Tabs.Panel value="repair">
          {periodLabel && (
            <Badge color="violet" variant="light" size="sm" mb="sm">📅 {periodLabel}</Badge>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.repairCostTrend", "Repair Cost — Last 6 Months")}</Text>
              <TrendBarChart data={repairByMonth} color="#6366f1" height={180} valuePrefix="฿" />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.repairsByType", "Repairs by Type")}</Text>
              <StatusDonut
                data={repairByType} total={filteredRepairs.length} height={180}
                colorMap={{ EXTERNAL: '#f59e0b', INTERNAL: '#0d9488', ON_SITE: '#3b82f6', WRITE_OFF: '#94a3b8' }}
                labelMap={{
                  EXTERNAL: t('reports.typeExternal', 'External'), INTERNAL: t('reports.typeInternal', 'Internal'),
                  ON_SITE: t('reports.typeOnSite', 'On Site'), WRITE_OFF: t('assetStatus.writeOff', 'Written Off'),
                }}
                centerLabel={t("reports.totalJobs", "Total Jobs")}
              />
            </Paper>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, md: 6 }} mb="md">
            <KPI label={t("reports.totalJobs","Total Jobs")} value={filteredRepairs.length} />
            <KPI label={t("reports.openJobs","Open")} value={repairOpen} color="#f59e0b" />
            <KPI label={t("reports.completedJobs","Completed")} value={repairDone} color="#0d9488" />
            <KPI label={t("reports.reportedDamaged","Reported Damaged")} value={repairDamaged} color="#ef4444" />
            <KPI label={t("reports.replaced","Replaced")} value={repairReplaced} color="#8b5cf6" />
            <KPI label={t("reports.estimatedCost","Est. Cost")} value={`฿${repairCost.toLocaleString()}`} color="#6366f1" />
          </SimpleGrid>
          <Group justify="flex-end" mb="sm" gap="xs">
            <Button size="xs" variant="light" color="gray" leftSection={<IconPrinter size={13} />}
              onClick={() => printRepairReport(filteredRepairs, companyLogoUrl, companyName, t)}>{t("common.print","Print")}</Button>
            <CSVBtn label="repair-report.csv" rows={filteredRepairs} columns={[
              { key: 'assetCode', label: 'Asset Code' }, { key: 'assetName', label: 'Asset Name' },
              { key: 'type', label: 'Type' }, { key: 'problem', label: 'Problem' },
              { key: 'vendorName', label: 'Vendor' }, { key: 'technicianName', label: 'Technician' },
              { key: 'status', label: 'Status' }, { key: 'estimatedCost', label: 'Est. Cost' },
              { key: 'resolution', label: 'Resolution' }, { key: 'createdAt', label: 'Date' },
            ]} />
          </Group>
          <Box id="rpt-repair">
            {isMobile ? (
              filteredRepairs.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noRepairJobs","No repair jobs")}</Text></Center>
              ) : (
                <Box>
                  {filteredRepairs.map((j: any) => (
                    <ReportCard key={j.id}
                      title={j.assetCode ?? '—'}
                      subtitle={j.assetName ?? '—'}
                      badge={<Badge size="xs" color={j.status==='OPEN'?'orange':j.status==='COMPLETED'?'teal':'gray'} variant="light">{j.status}</Badge>}
                      details={[
                        { label: 'Type', value: <Badge size="xs" variant="light" color="blue">{j.type}</Badge> },
                        { label: 'Problem', value: j.problem ?? '—' },
                        { label: 'Vendor / Tech', value: j.vendorName ?? j.technicianName ?? '—' },
                        { label: 'Est. Cost', value: j.estimatedCost ? `฿${Number(j.estimatedCost).toLocaleString()}` : '—' },
                        { label: 'Date', value: j.createdAt?.slice(0,10) ?? '—' },
                      ]}
                    />
                  ))}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['Asset Code','Asset Name','Type','Problem','Vendor / Tech','Status','Est. Cost','Date'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredRepairs.length === 0
                    ? <Table.Tr><Table.Td colSpan={8}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noRepairJobs","No repair jobs")}</Text></Center></Table.Td></Table.Tr>
                    : filteredRepairs.map((j: any) => (
                      <Table.Tr key={j.id}>
                        <Table.Td><Text size="xs" fw={600}>{j.assetCode ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{j.assetName ?? '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" variant="light" color="blue">{j.type}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed" lineClamp={2} style={{ maxWidth: 160 }}>{j.problem ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{j.vendorName ?? j.technicianName ?? '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={j.status==='OPEN'?'orange':j.status==='COMPLETED'?'teal':'gray'} variant="light">{j.status}</Badge></Table.Td>
                        <Table.Td><Text size="xs">{j.estimatedCost ? `฿${Number(j.estimatedCost).toLocaleString()}` : '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{j.createdAt?.slice(0,10) ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── WORK ORDERS / HK ORDERS ─────────────────────────────── */}
        {/* ตั้งใจเว้นว่างไว้ — ไม่ดึงข้อมูลใดๆ (การสรุปย้ายไปอยู่ที่ แดชบอร์ดออเดอร์ แล้ว)
            ส่วนนี้จะทำเป็นแถบค้นหา + Filter เลือกช่วงเวลา/หัวข้อ แล้ว Export Excel/PDF ในภายหลัง */}
        <Tabs.Panel value="workorders">
          <Center p="xl">
            <Text size="sm" c="dimmed">{t('reports.workOrdersComingSoon', 'อยู่ระหว่างเตรียมหน้ารายงานงาน/คำขอ (ค้นหา + กรอง + Export) — ดูภาพรวมสรุปได้ที่หน้า แดชบอร์ดออเดอร์')}</Text>
          </Center>
        </Tabs.Panel>

        {/* ─── BORROW ──────────────────────────────────────────────── */}
        <Tabs.Panel value="borrow">
          {periodLabel && (
            <Badge color="violet" variant="light" size="sm" mb="sm">📅 {periodLabel}</Badge>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.borrowByStatusChart", "Borrow Requests by Status")}</Text>
              <StatusDonut
                data={borrowByStatus} total={filteredBorrows.length} height={180}
                colorMap={{ PENDING: '#f59e0b', APPROVED: '#3b82f6', PARTIALLY_RETURNED: '#6366f1', OVERDUE: '#ef4444', RETURNED: '#0d9488', REJECTED: '#94a3b8' }}
                labelMap={{
                  PENDING: t('common.pending', 'Pending'), APPROVED: t('reports.approved', 'Approved'),
                  PARTIALLY_RETURNED: t('reports.partiallyReturned', 'Partially Returned'), OVERDUE: t('common.overdue', 'Overdue'),
                  RETURNED: t('reports.returned', 'Returned'), REJECTED: t('common.reject', 'Rejected'),
                }}
                centerLabel={t("common.total", "Total")}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.topBorrowers", "Top Borrowers")}</Text>
              <TopBarChart data={borrowByPerson} limit={7} color="#3b82f6" height={180} />
            </Paper>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
            <KPI label={t("common.total","Total")} value={filteredBorrows.length} />
            <KPI label={t("common.pending","Pending")} value={borrowPending} color="#f59e0b" />
            <KPI label={t("common.active","Active")} value={borrowActive} color="#3b82f6" />
            <KPI label={t("common.overdue","Overdue")} value={borrowOverdue} color="#ef4444" />
          </SimpleGrid>
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-borrow" title="Borrow Report" />
            <CSVBtn label="borrow-report.csv" rows={filteredBorrows} columns={[
              { key: 'id', label: '#' }, { key: 'borrowerName', label: 'Borrower' },
              { key: 'reason', label: 'Reason' }, { key: 'dueDate', label: 'Due Date' },
              { key: 'status', label: 'Status' }, { key: 'createdAt', label: 'Created' },
            ]} />
          </Group>
          <Box id="rpt-borrow">
            {isMobile ? (
              filteredBorrows.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noRecords","No records")}</Text></Center>
              ) : (
                <Box>
                  {filteredBorrows.map((b: any) => (
                    <ReportCard key={b.id}
                      title={`#${b.id} — ${b.borrowerName ?? '—'}`}
                      subtitle={(b.assets ?? []).map((a: any) => a.assetCode).join(', ') || '—'}
                      badge={<Badge size="xs" color={BORROW_COLOR[b.status]??'gray'} variant="light">{b.status}</Badge>}
                      details={[
                        { label: 'Reason', value: b.reason || '—' },
                        { label: 'Due Date', value: <Text size="xs" c={b.status==='OVERDUE'?'red':'dimmed'}>{b.dueDate?.slice(0,10) || '—'}</Text> },
                        { label: 'Created', value: b.createdAt?.slice(0,10) ?? '—' },
                      ]}
                    />
                  ))}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['#','Assets','Borrower','Reason','Due Date','Status','Created'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredBorrows.length === 0
                    ? <Table.Tr><Table.Td colSpan={7}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noRecords","No records")}</Text></Center></Table.Td></Table.Tr>
                    : filteredBorrows.map((b: any) => (
                      <Table.Tr key={b.id}>
                        <Table.Td><Text size="xs" fw={600}>#{b.id}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{(b.assets ?? []).map((a: any) => a.assetCode).join(', ') || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{b.borrowerName ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed" lineClamp={1} style={{ maxWidth: 130 }}>{b.reason || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c={b.status==='OVERDUE'?'red':'dimmed'}>{b.dueDate?.slice(0,10) || '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={BORROW_COLOR[b.status]??'gray'} variant="light">{b.status}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{b.createdAt?.slice(0,10) ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── HANDOVER ────────────────────────────────────────────── */}
        <Tabs.Panel value="handover">
          {periodLabel && (
            <Badge color="violet" variant="light" size="sm" mb="sm">📅 {periodLabel}</Badge>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.handoverByStatusChart", "Handovers by Status")}</Text>
              <StatusDonut
                data={handoverByStatus} total={filteredHandovers.length} height={170}
                colorMap={{ DRAFT: '#f59e0b', RECEIVED: '#0d9488', CANCELLED: '#94a3b8' }}
                labelMap={{ DRAFT: t('common.pending', 'Pending'), RECEIVED: t('common.approved', 'Approved'), CANCELLED: t('common.cancel', 'Cancelled') }}
                centerLabel={t("common.total", "Total")}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.handoverByDeptChart", "Handovers by Department")}</Text>
              <TopBarChart data={handoverByDept} limit={7} color="#6366f1" height={170} />
            </Paper>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, md: 3 }} mb="md">
            <KPI label={t("common.total","Total")} value={filteredHandovers.length} />
            <KPI label={t("common.approved","Approved")} value={hoApproved} color="#0d9488" />
            <KPI label={t("common.pending","Pending")} value={hoDraft} color="#f59e0b" />
          </SimpleGrid>
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-handover" title="Handover Report" />
            <CSVBtn label="handover-report.csv" rows={filteredHandovers} columns={[
              { key: 'id', label: '#' }, { key: 'recipientName', label: 'Recipient' },
              { key: 'departmentCode', label: 'Department' }, { key: 'status', label: 'Status' },
              { key: 'handoverDate', label: 'Date' }, { key: 'createdBy', label: 'Created By' },
            ]} />
          </Group>
          <Box id="rpt-handover">
            {isMobile ? (
              filteredHandovers.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noRecords","No records")}</Text></Center>
              ) : (
                <Box>
                  {filteredHandovers.map((h: any) => (
                    <ReportCard key={h.id}
                      title={`#${h.id} — ${h.recipientName ?? '—'}`}
                      subtitle={(h.assets ?? []).map((a: any) => a.assetCode).join(', ') || '—'}
                      badge={<Badge size="xs" color={h.status==='RECEIVED'?'teal':h.status==='DRAFT'?'yellow':'gray'} variant="light">{h.status}</Badge>}
                      details={[
                        { label: 'Department', value: h.departmentCode ?? '—' },
                        { label: 'Date', value: h.handoverDate?.slice(0,10) ?? h.createdAt?.slice(0,10) ?? '—' },
                      ]}
                    />
                  ))}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['#','Assets','Recipient','Department','Status','Date'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredHandovers.length === 0
                    ? <Table.Tr><Table.Td colSpan={6}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noRecords","No records")}</Text></Center></Table.Td></Table.Tr>
                    : filteredHandovers.map((h: any) => (
                      <Table.Tr key={h.id}>
                        <Table.Td><Text size="xs" fw={600}>#{h.id}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{(h.assets ?? []).map((a: any) => a.assetCode).join(', ') || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{h.recipientName ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{h.departmentCode ?? '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={h.status==='RECEIVED'?'teal':h.status==='DRAFT'?'yellow':'gray'} variant="light">{h.status}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{h.handoverDate?.slice(0,10) ?? h.createdAt?.slice(0,10) ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── TRANSFER ────────────────────────────────────────────── */}
        <Tabs.Panel value="transfer">
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.transferByStatusChart", "Transfers by Status")}</Text>
              <StatusDonut
                data={transferByStatus} total={filteredTransfers.length} height={170}
                colorMap={{ PENDING: '#f59e0b', APPROVED: '#0d9488', REJECTED: '#ef4444' }}
                labelMap={{ PENDING: t('common.pending', 'Pending'), APPROVED: t('common.approved', 'Approved'), REJECTED: t('common.reject', 'Rejected') }}
                centerLabel={t("common.total", "Total")}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.transferByDeptChart", "Transfers by Source Department")}</Text>
              <TopBarChart data={transferByFromDept} limit={7} color="#f59e0b" height={170} />
            </Paper>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, md: 3 }} mb="md">
            <KPI label={t("common.total","Total")} value={filteredTransfers.length} />
            <KPI label={t("common.pending","Pending")} value={trPending} color="#f59e0b" />
            <KPI label={t("common.approved","Approved")} value={trApproved} color="#0d9488" />
          </SimpleGrid>
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-transfer" title="Transfer Report" />
            <CSVBtn label="transfer-report.csv" rows={filteredTransfers} columns={[
              { key: 'id', label: '#' }, { key: 'fromDeptCode', label: 'From Dept' },
              { key: 'toDeptCode', label: 'To Dept' }, { key: 'requestedBy', label: 'Requested By' },
              { key: 'status', label: 'Status' }, { key: 'createdAt', label: 'Date' },
            ]} />
          </Group>
          <Box id="rpt-transfer">
            {isMobile ? (
              filteredTransfers.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noTransferRequests","No transfer requests")}</Text></Center>
              ) : (
                <Box>
                  {filteredTransfers.map((t2: any) => (
                    <ReportCard key={t2.id}
                      title={`#${t2.id} — ${t2.fromDeptCode ?? '—'} → ${t2.toDeptCode ?? '—'}`}
                      subtitle={(t2.assets ?? []).map((a: any) => a.assetCode).join(', ') || '—'}
                      badge={<Badge size="xs" color={t2.status==='APPROVED'?'teal':t2.status==='PENDING'?'yellow':'gray'} variant="light">{t2.status}</Badge>}
                      details={[
                        { label: 'Requested By', value: t2.requestedBy ?? '—' },
                        { label: 'Date', value: t2.createdAt?.slice(0,10) ?? '—' },
                      ]}
                    />
                  ))}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['#','Assets','From','To','Requested By','Status','Date'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredTransfers.length === 0
                    ? <Table.Tr><Table.Td colSpan={7}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noTransferRequests","No transfer requests")}</Text></Center></Table.Td></Table.Tr>
                    : filteredTransfers.map((t2: any) => (
                      <Table.Tr key={t2.id}>
                        <Table.Td><Text size="xs" fw={600}>#{t2.id}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{(t2.assets ?? []).map((a: any) => a.assetCode).join(', ') || '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{t2.fromDeptCode ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{t2.toDeptCode ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{t2.requestedBy ?? '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={t2.status==='APPROVED'?'teal':t2.status==='PENDING'?'yellow':'gray'} variant="light">{t2.status}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{t2.createdAt?.slice(0,10) ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── INVENTORY ───────────────────────────────────────────── */}
        <Tabs.Panel value="inventory">
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.stockStatusChart", "Stock Status")}</Text>
              <StatusDonut
                data={invByStockStatus} total={filteredInv.length} height={170}
                colorMap={{ NORMAL: '#0d9488', LOW: '#f59e0b', OUT: '#ef4444' }}
                labelMap={{ NORMAL: t('reports.stockNormal', 'Normal'), LOW: t('reports.lowStock', 'Low Stock'), OUT: t('reports.outOfStock', 'Out of Stock') }}
                centerLabel={t("common.items", "Items")}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.valueByCategoryChart", "Stock Value by Category")}</Text>
              <TopBarChart data={invByCategoryValue} limit={7} color="#0d9488" height={170} />
            </Paper>
          </SimpleGrid>
          {invSummary && (
            <SimpleGrid cols={{ base: 2, md: 4 }} mb="md">
              <KPI label={t("common.items","Items")} value={invSummary.totalItems ?? filteredInv.length} />
              <KPI label={t("reports.totalValue","Total Value")} value={`฿${Number(invSummary.totalValue ?? invVal).toLocaleString()}`} color="#0d9488" />
              <KPI label={t("reports.lowStock","Low Stock")} value={invSummary.lowStockCount ?? invLow} color="#f59e0b" />
              <KPI label={t("reports.outOfStock","Out of Stock")} value={invSummary.outOfStockCount ?? invOut} color="#ef4444" />
            </SimpleGrid>
          )}
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-inventory" title="Inventory Report" />
            <CSVBtn label="inventory-report.csv" rows={filteredInv} columns={[
              { key: 'sku', label: 'SKU' }, { key: 'name', label: 'Name' },
              { key: 'category', label: 'Category' }, { key: 'brand', label: 'Brand' },
              { key: 'stockQty', label: 'Stock' }, { key: 'minStock', label: 'Min Stock' },
              { key: 'unitCost', label: 'Unit Cost' }, { key: 'unit', label: 'Unit' },
            ]} />
          </Group>
          <Box id="rpt-inventory">
            {isMobile ? (
              filteredInv.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noItems","No items")}</Text></Center>
              ) : (
                <Box>
                  {filteredInv.map((i: any) => {
                    const isLow = (i.stockQty??0) <= (i.minStock??0) && (i.stockQty??0) > 0
                    const isOut = (i.stockQty??0) === 0
                    return (
                      <ReportCard key={i.id}
                        title={i.name}
                        subtitle={i.sku}
                        badge={<Badge size="xs" color={isOut?'red':isLow?'orange':'teal'} variant="light">{isOut?'Out':isLow?'Low':'OK'}</Badge>}
                        details={[
                          { label: 'Category', value: i.category ?? '—' },
                          { label: 'Stock', value: <Text size="xs" fw={600} c={isOut?'red':isLow?'orange':'teal'}>{i.stockQty ?? 0}</Text> },
                          { label: 'Min Stock', value: i.minStock ?? 0 },
                          { label: 'Unit Cost', value: `฿${Number(i.unitCost ?? 0).toLocaleString()}` },
                          { label: 'Total Value', value: `฿${((i.stockQty??0)*(i.unitCost??0)).toLocaleString()}` },
                        ]}
                      />
                    )
                  })}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['SKU','Name','Category','Stock','Min','Unit Cost','Total Value','Status'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredInv.length === 0
                    ? <Table.Tr><Table.Td colSpan={8}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noItems","No items")}</Text></Center></Table.Td></Table.Tr>
                    : filteredInv.map((i: any) => {
                        const isLow = (i.stockQty??0) <= (i.minStock??0) && (i.stockQty??0) > 0
                        const isOut = (i.stockQty??0) === 0
                        return (
                          <Table.Tr key={i.id}>
                            <Table.Td><Text size="xs" c="dimmed">{i.sku}</Text></Table.Td>
                            <Table.Td><Text size="xs" fw={500}>{i.name}</Text></Table.Td>
                            <Table.Td><Text size="xs" c="dimmed">{i.category ?? '—'}</Text></Table.Td>
                            <Table.Td><Text size="xs" fw={600} c={isOut?'red':isLow?'orange':'teal'}>{i.stockQty ?? 0}</Text></Table.Td>
                            <Table.Td><Text size="xs" c="dimmed">{i.minStock ?? 0}</Text></Table.Td>
                            <Table.Td><Text size="xs">฿{Number(i.unitCost ?? 0).toLocaleString()}</Text></Table.Td>
                            <Table.Td><Text size="xs">฿{((i.stockQty??0)*(i.unitCost??0)).toLocaleString()}</Text></Table.Td>
                            <Table.Td><Badge size="xs" color={isOut?'red':isLow?'orange':'teal'} variant="light">{isOut?'Out':isLow?'Low':'OK'}</Badge></Table.Td>
                          </Table.Tr>
                        )
                      })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── LICENSE ─────────────────────────────────────────────── */}
        <Tabs.Panel value="license">
          {periodLabel && (
            <Badge color="violet" variant="light" size="sm" mb="sm">📅 {periodLabel}</Badge>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.licenseByStatusChart", "Licenses by Status")}</Text>
              <StatusDonut
                data={filteredLics.reduce((acc: any, l: any) => { acc[l.status] = (acc[l.status]||0)+1; return acc }, {})}
                total={filteredLics.length} height={170}
                colorMap={{ ACTIVE: '#0d9488', EXPIRING: '#f59e0b', EXPIRED: '#ef4444', INACTIVE: '#94a3b8' }}
                labelMap={{ ACTIVE: t('common.active','Active'), EXPIRING: t('reports.expiring','Expiring'), EXPIRED: t('reports.expired','Expired'), INACTIVE: t('common.inactive','Inactive') }}
                centerLabel={t("common.total", "Total")}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.costByVendorChart", "Cost by Vendor")}</Text>
              <TopBarChart data={filteredLics.reduce((acc: any, l: any) => { const k = l.vendor || t('reports.unspecified','Unspecified'); acc[k] = (acc[k]||0) + Number(l.cost||0); return acc }, {})} limit={7} color="#6366f1" height={170} />
            </Paper>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, md: 5 }} mb="md">
            <KPI label={periodLabel ? 'Licenses in Period' : 'Total'} value={filteredLics.length} />
            <KPI label={t("common.active","Active")} value={filteredLics.filter(l => l.status === 'ACTIVE').length} color="#0d9488" />
            <KPI label={t("reports.expiring","Expiring")} value={filteredLics.filter(l => l.status === 'EXPIRING').length} color="#f59e0b" />
            <KPI label={t("reports.expired","Expired")} value={filteredLics.filter(l => l.status === 'EXPIRED').length} color="#ef4444" />
            <KPI label={t("reports.totalCost","Total Cost")} value={`฿${filteredLics.reduce((s, l) => s + Number(l.cost || 0), 0).toLocaleString()}`} color="#6366f1" />
          </SimpleGrid>
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-license" title="License Report" />
            <CSVBtn label="license-report.csv" rows={filteredLics} columns={[
              { key: 'productName', label: 'Product' }, { key: 'type', label: 'Type' },
              { key: 'vendor', label: 'Vendor' }, { key: 'totalSeats', label: 'Total Seats' },
              { key: 'usedSeats', label: 'Used Seats' }, { key: 'expiryDate', label: 'Expiry' },
              { key: 'cost', label: 'Cost' }, { key: 'status', label: 'Status' },
            ]} />
          </Group>
          <Box id="rpt-license">
            {isMobile ? (
              filteredLics.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noLicenses","No licenses")}</Text></Center>
              ) : (
                <Box>
                  {filteredLics.map((l: any) => {
                    const days = l.daysUntilExpiry
                    return (
                      <ReportCard key={l.id}
                        title={l.productName}
                        subtitle={l.version ? `v${l.version}` : l.vendor || '—'}
                        badge={<Badge size="xs" color={LIC_COLOR[l.status]??'gray'} variant="light">{l.status}</Badge>}
                        details={[
                          { label: 'Type', value: <Badge size="xs" variant="light" color="blue">{l.type}</Badge> },
                          { label: 'Vendor', value: l.vendor || '—' },
                          { label: 'Seats', value: l.totalSeats > 0 ? `${l.usedSeats}/${l.totalSeats}` : '∞' },
                          { label: 'Expiry', value: l.expiryDate
                              ? <Text size="xs" c={days!==null&&days<=30?'red':days!==null&&days<=90?'orange':'dimmed'} fw={days!==null&&days<=30?600:400}>
                                  {days===null ? l.expiryDate.slice(0,10) : days<0 ? 'EXPIRED' : `${days}d`}
                                </Text>
                              : t("reports.noExpiry","No expiry") },
                          { label: 'Cost', value: l.cost ? `฿${Number(l.cost).toLocaleString()}` : '—' },
                        ]}
                      />
                    )
                  })}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['Product','Type','Vendor','Seats','Expiry','Cost','Status'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredLics.length === 0
                    ? <Table.Tr><Table.Td colSpan={7}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noLicenses","No licenses")}</Text></Center></Table.Td></Table.Tr>
                    : filteredLics.map((l: any) => {
                        const days = l.daysUntilExpiry
                        return (
                          <Table.Tr key={l.id}>
                            <Table.Td>
                              <Text size="xs" fw={600}>{l.productName}</Text>
                              {l.version && <Text size="xs" c="dimmed">v{l.version}</Text>}
                            </Table.Td>
                            <Table.Td><Badge size="xs" variant="light" color="blue">{l.type}</Badge></Table.Td>
                            <Table.Td><Text size="xs" c="dimmed">{l.vendor || '—'}</Text></Table.Td>
                            <Table.Td>
                              <Text size="xs">{l.totalSeats > 0 ? `${l.usedSeats}/${l.totalSeats}` : '∞'}</Text>
                              {l.totalSeats > 0 && (
                                <Progress value={(l.usedSeats/l.totalSeats)*100} size={3}
                                  color={l.usedSeats>=l.totalSeats?'red':'teal'} mt={2} style={{ minWidth: 50 }} />
                              )}
                            </Table.Td>
                            <Table.Td>
                              {l.expiryDate
                                ? <Text size="xs" c={days!==null&&days<=30?'red':days!==null&&days<=90?'orange':'dimmed'} fw={days!==null&&days<=30?600:400}>
                                    {days===null ? l.expiryDate.slice(0,10) : days<0 ? 'EXPIRED' : `${days}d`}
                                  </Text>
                                : <Text size="xs" c="dimmed">{t("reports.noExpiry","No expiry")}</Text>}
                            </Table.Td>
                            <Table.Td><Text size="xs">{l.cost ? `฿${Number(l.cost).toLocaleString()}` : '—'}</Text></Table.Td>
                            <Table.Td><Badge size="xs" color={LIC_COLOR[l.status]??'gray'} variant="light">{l.status}</Badge></Table.Td>
                          </Table.Tr>
                        )
                      })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── ASSET CHECKING ──────────────────────────────────────── */}
        <Tabs.Panel value="checking">
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.passFailChart", "Overall Pass / Fail Rate")}</Text>
              <StatusDonut
                data={{
                  PASS: checkSessions.reduce((s: number, x: any) => s + (x.passCount ?? 0), 0),
                  FAIL: checkSessions.reduce((s: number, x: any) => s + (x.failCount ?? 0), 0),
                }}
                total={checkSessions.reduce((s: number, x: any) => s + (x.totalItems ?? 0), 0)}
                height={170}
                colorMap={{ PASS: '#0d9488', FAIL: '#ef4444' }}
                labelMap={{ PASS: t('common.pass', 'Pass'), FAIL: t('common.fail', 'Fail') }}
                centerLabel={t("common.items", "Items")}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Text fw={600} size="sm" mb="md">{t("reports.sessionsByChecker", "Sessions by Checker")}</Text>
              <TopBarChart data={checkSessions.reduce((acc: any, s: any) => { const k = s.checkedBy || t('reports.unspecified','Unspecified'); acc[k] = (acc[k]||0)+1; return acc }, {})} limit={7} color="#3b82f6" height={170} />
            </Paper>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 2, md: 3 }} mb="md">
            <KPI label={t("reports.totalSessions","Total Sessions")} value={checkSessions.length} />
            <KPI label={t("reports.completedJobs","Completed")} value={csCompleted} color="#0d9488" />
            <KPI label={t("common.pending","Pending")} value={checkSessions.length - csCompleted} color="#f59e0b" />
          </SimpleGrid>
          <Group justify="flex-end" mb="sm" gap="xs">
            <PrintBtn targetId="rpt-checking" title="Asset Checking Report" />
            <CSVBtn label="checking-report.csv" rows={checkSessions} columns={[
              { key: 'id', label: '#' }, { key: 'comsetName', label: 'COMSET' },
              { key: 'status', label: 'Status' }, { key: 'checkedBy', label: 'Checked By' },
              { key: 'totalItems', label: 'Total Items' }, { key: 'passCount', label: 'Pass' },
              { key: 'failCount', label: 'Fail' }, { key: 'createdAt', label: 'Date' },
            ]} />
          </Group>
          <Box id="rpt-checking">
            {isMobile ? (
              checkSessions.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed">{t("reports.noCheckSessions","No check sessions")}</Text></Center>
              ) : (
                <Box>
                  {checkSessions.map((s: any) => (
                    <ReportCard key={s.id}
                      title={`#${s.id} — ${s.comsetName ?? s.comset?.name ?? '—'}`}
                      subtitle={s.checkedBy ?? s.performedBy ?? '—'}
                      badge={<Badge size="xs" color={s.status==='COMPLETED'?'teal':'yellow'} variant="light">{s.status}</Badge>}
                      details={[
                        { label: 'Total Items', value: s.totalItems ?? (s.results?.length ?? '—') },
                        { label: 'Pass', value: <Text size="xs" c="teal" fw={600}>{s.passCount ?? '—'}</Text> },
                        { label: 'Fail', value: <Text size="xs" c="red" fw={600}>{s.failCount ?? '—'}</Text> },
                        { label: 'Date', value: s.createdAt?.slice(0,10) ?? '—' },
                      ]}
                    />
                  ))}
                </Box>
              )
            ) : (
            <ScrollArea>
              <Table highlightOnHover>
                <Table.Thead style={{ background: '#f8fafc' }}>
                  <Table.Tr>
                    {['#','COMSET','Status','Checked By','Total','Pass','Fail','Date'].map(h => (
                      <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {checkSessions.length === 0
                    ? <Table.Tr><Table.Td colSpan={8}><Center p="xl"><Text size="sm" c="dimmed">{t("reports.noCheckSessions","No check sessions")}</Text></Center></Table.Td></Table.Tr>
                    : checkSessions.map((s: any) => (
                      <Table.Tr key={s.id}>
                        <Table.Td><Text size="xs" fw={600}>#{s.id}</Text></Table.Td>
                        <Table.Td><Text size="xs">{s.comsetName ?? s.comset?.name ?? '—'}</Text></Table.Td>
                        <Table.Td><Badge size="xs" color={s.status==='COMPLETED'?'teal':'yellow'} variant="light">{s.status}</Badge></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{s.checkedBy ?? s.performedBy ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs">{s.totalItems ?? (s.results?.length ?? '—')}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="teal" fw={600}>{s.passCount ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="red" fw={600}>{s.failCount ?? '—'}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dimmed">{s.createdAt?.slice(0,10) ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
          </Box>
        </Tabs.Panel>

        {/* ─── DEPRECIATION ────────────────────────────────────────── */}
        <Tabs.Panel value="depreciation">
          <Group mb="md" gap="md" align="flex-end" wrap="wrap">
            <Select label={t("common.method","Method")} value={deprMethod} onChange={v => setDeprMethod(v ?? 'STRAIGHT_LINE')}
              data={[{ value: 'STRAIGHT_LINE', label: 'Straight Line' }, { value: 'DECLINING_BALANCE', label: 'Declining Balance' }]}
              style={{ width: isMobile ? '100%' : 200 }} size="sm" />
            <Select label={t("reports.usefulLife","Useful Life")} value={String(deprLife)} onChange={v => setDeprLife(Number(v))}
              data={['3','4','5','7','10','15','20'].map(v => ({ value: v, label: `${v} years` }))}
              style={{ width: isMobile ? '100%' : 120 }} size="sm" />
            <PrintBtn targetId="rpt-depr" title="Depreciation Report" />
            {depr && (
              <CSVBtn label="depreciation-report.csv" rows={depr.items ?? []} columns={[
                { key: 'assetCode', label: 'Code' }, { key: 'name', label: 'Name' },
                { key: 'purchasePrice', label: 'Purchase Price' }, { key: 'purchaseDate', label: 'Purchase Date' },
                { key: 'ageYears', label: 'Age (yrs)' }, { key: 'accumulatedDepreciation', label: 'Accumulated Depr.' },
                { key: 'bookValue', label: 'Book Value' },
              ]} />
            )}
          </Group>
          {depr && (
            <>
              <SimpleGrid cols={{ base: 1, md: 2 }} mb="lg">
                <Paper withBorder radius="md" p="md">
                  <Text fw={600} size="sm" mb="md">{t("reports.valueBreakdownChart", "Book Value vs Depreciated")}</Text>
                  <StatusDonut
                    data={{
                      BOOK_VALUE: Number(depr.totalBookValue ?? 0),
                      DEPRECIATED: (depr.items ?? []).reduce((s: number, i: any) => s + Number(i.accumulatedDepreciation ?? 0), 0),
                    }}
                    total={(depr.items ?? []).reduce((s: number, i: any) => s + Number(i.purchasePrice ?? 0), 0)}
                    height={170}
                    colorMap={{ BOOK_VALUE: '#0d9488', DEPRECIATED: '#94a3b8' }}
                    labelMap={{ BOOK_VALUE: t('reports.remainingValue', 'Remaining Book Value'), DEPRECIATED: t('reports.accumulatedDepreciation', 'Accumulated Depreciation') }}
                    centerLabel={t("reports.totalPurchaseCost", "Total Purchase Cost")}
                  />
                </Paper>
                <Paper withBorder radius="md" p="md">
                  <Text fw={600} size="sm" mb="md">{t("reports.topBookValueChart", "Top Assets by Book Value")}</Text>
                  <TopBarChart
                    data={Object.fromEntries((depr.items ?? []).slice().sort((a: any, b: any) => Number(b.bookValue ?? 0) - Number(a.bookValue ?? 0)).slice(0, 7).map((i: any) => [i.assetCode ?? i.name, Number(i.bookValue ?? 0)]))}
                    limit={7} color="#6366f1" height={170}
                  />
                </Paper>
              </SimpleGrid>
              <SimpleGrid cols={{ base: 2, md: 3 }} mb="md">
                <KPI label={t("reports.assetsWithPrice","Assets with Price")} value={depr.assetsWithPrice} />
                <KPI label={t("reports.totalBookValue","Total Book Value")} value={`฿${Number(depr.totalBookValue ?? 0).toLocaleString()}`} color="#0d9488" />
                <KPI label={t("reports.usefulLife","Useful Life")} value={`${deprLife} years`} color="#6366f1" />
              </SimpleGrid>
              <Box id="rpt-depr">
                {isMobile ? (
                  (depr.items ?? []).length === 0 ? (
                    <Center p="xl"><Text size="sm" c="dimmed">{t("common.noRecords","No records")}</Text></Center>
                  ) : (
                    <Box>
                      {(depr.items ?? []).map((a: any) => (
                        <ReportCard key={a.id}
                          title={a.assetCode}
                          subtitle={a.name}
                          badge={<Text size="xs" fw={600} c="teal">฿{Number(a.bookValue).toLocaleString()}</Text>}
                          details={[
                            { label: 'Purchase Price', value: `฿${Number(a.purchasePrice).toLocaleString()}` },
                            { label: 'Purchase Date', value: a.purchaseDate?.slice(0,10) },
                            { label: 'Age (yrs)', value: Number(a.ageYears).toFixed(1) },
                            { label: 'Accum. Depr.', value: <Text size="xs" c="orange">฿{Number(a.accumulatedDepreciation).toLocaleString()}</Text> },
                          ]}
                        />
                      ))}
                    </Box>
                  )
                ) : (
                <ScrollArea>
                  <Table highlightOnHover>
                    <Table.Thead style={{ background: '#f8fafc' }}>
                      <Table.Tr>
                        {['Code','Name','Purchase Price','Purchase Date','Age (yrs)','Accumulated Depr.','Book Value'].map(h => (
                          <Table.Th key={h} style={thStyle}>{h}</Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(depr.items ?? []).map((a: any) => (
                        <Table.Tr key={a.id}>
                          <Table.Td><Text size="xs" fw={600}>{a.assetCode}</Text></Table.Td>
                          <Table.Td><Text size="xs">{a.name}</Text></Table.Td>
                          <Table.Td><Text size="xs">฿{Number(a.purchasePrice).toLocaleString()}</Text></Table.Td>
                          <Table.Td><Text size="xs" c="dimmed">{a.purchaseDate?.slice(0,10)}</Text></Table.Td>
                          <Table.Td><Text size="xs">{Number(a.ageYears).toFixed(1)}</Text></Table.Td>
                          <Table.Td><Text size="xs" c="orange">฿{Number(a.accumulatedDepreciation).toLocaleString()}</Text></Table.Td>
                          <Table.Td><Text size="xs" fw={600} c="teal">฿{Number(a.bookValue).toLocaleString()}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
                )}
              </Box>
            </>
          )}
        </Tabs.Panel>
      </Tabs>
    </Box>
  )
}
